import { z } from "zod";
import { HelpdeskDestinationCondition } from "./helpdesk-conditions.ts";

export { HelpdeskConditionGroup, HelpdeskConditionOperator, HelpdeskConditionPredicate, HelpdeskDestinationCondition, HelpdeskGroupConditionQuantifier } from "./helpdesk-conditions.ts";

import { HelpdeskApprovalInteropConfig } from "./helpdesk-approval-interop.ts";

export {
  HelpdeskApprovalInteropConfig,
  HelpdeskApprovalInteropIntent,
  HelpdeskApprovalInteropProvenance,
  HelpdeskApprovalInteropRule,
  HelpdeskApprovalInteropSource,
} from "./helpdesk-approval-interop.ts";

import {
  defaultHelpdeskCloudInfrastructureRoleTemplates,
  HelpdeskCloudInfrastructureRecipientRules,
  HelpdeskCloudInfrastructureRoleTemplates,
  migrateHelpdeskCloudInfrastructureRecipientRules,
} from "./cloud-infrastructure.ts";

export {
  defaultHelpdeskCloudInfrastructureRoleTemplates,
  HELPDESK_CLOUD_INFRASTRUCTURE_ROLE_TEMPLATE_TOKENS,
  HelpdeskCloudInfrastructureGroupRule,
  HelpdeskCloudInfrastructureRecipientRule,
  HelpdeskCloudInfrastructureRecipientRules,
  HelpdeskCloudInfrastructureRoleTemplate,
  HelpdeskCloudInfrastructureRoleTemplates,
  migrateHelpdeskCloudInfrastructureRecipientRules,
} from "./cloud-infrastructure.ts";

import { LINEAR_CONVERSATION_MAX_ATTACHMENT_BYTES, LINEAR_CONVERSATION_MAX_ATTACHMENTS, LINEAR_CONVERSATION_MAX_REQUEST_BYTES, LinearConversationAttachmentFileName } from "./conversation.ts";
import {
  HelpdeskExternalResourceBrowser,
  HelpdeskExternalResourceMaterialized,
  HelpdeskExternalResourceSemantic,
  HelpdeskExternalResourceSource,
  HelpdeskExternalResourceSourceIdentity,
  HelpdeskExternalResourceTypedValue,
  HelpdeskPluginAuthoringDescriptor,
  HelpdeskPluginComponentControl,
} from "./helpdesk-plugins.ts";
import { EntraGroupRef, PersonRef } from "./people.ts";

const NonEmptyString = z.string().trim().min(1);
const Timestamp = z.string().datetime({ offset: true });
const semanticIdPattern = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/;

export const HELPDESK_FORM_NAME_MAX_LENGTH = 200;
export const HELPDESK_FORM_DESCRIPTION_MAX_LENGTH = 10_000;
export const HELPDESK_WEBHOOK_NAME_MAX_LENGTH = 200;
export const HELPDESK_LINEAR_SHARE_RECIPIENT_MAX = 20;

const HelpdeskFormName = NonEmptyString.max(HELPDESK_FORM_NAME_MAX_LENGTH);
const HelpdeskFormDescription = z.string().trim().max(HELPDESK_FORM_DESCRIPTION_MAX_LENGTH).nullable();
const HelpdeskWebhookName = NonEmptyString.max(HELPDESK_WEBHOOK_NAME_MAX_LENGTH);

export const HelpdeskSemanticId = z.string().trim().min(1).max(200).regex(semanticIdPattern);
export type HelpdeskSemanticId = z.infer<typeof HelpdeskSemanticId>;

export const HelpdeskCommunicationMode = z.enum(["conversation", "thread", "status_only"]);
export type HelpdeskCommunicationMode = z.infer<typeof HelpdeskCommunicationMode>;

export const HelpdeskStatusEmailMode = z.enum(["all", "finished", "none"]);
export type HelpdeskStatusEmailMode = z.infer<typeof HelpdeskStatusEmailMode>;

export const HELPDESK_CONVERSATION_MODES = ["conversation", "thread"] as const;

export const hasRequesterConversation = (mode: HelpdeskCommunicationMode): boolean => (HELPDESK_CONVERSATION_MODES as readonly string[]).includes(mode);
export const isThreadMode = (mode: HelpdeskCommunicationMode): boolean => mode === "thread";

// Single source of truth for the field types: the union below derives every discriminator from this enum, so a new
// type cannot be added to one and forgotten in the other. It stays a standalone enum because the owner-facing form
// editor needs the choices as a runtime-enumerable list for its "add field" control, which the union cannot express.
export const HelpdeskFormFieldType = z.enum(["text", "textarea", "date", "textBlock", "choice", "person", "email", "number", "dateTime", "confirmation", "attachment", "externalResource", "group"]);
export type HelpdeskFormFieldType = z.infer<typeof HelpdeskFormFieldType>;

export const HELPDESK_FIELD_TYPE_CAPABILITIES: Readonly<Record<HelpdeskFormFieldType, { readonly internalOnly: boolean }>> = {
  attachment: { internalOnly: false },
  choice: { internalOnly: false },
  confirmation: { internalOnly: false },
  date: { internalOnly: false },
  dateTime: { internalOnly: false },
  email: { internalOnly: false },
  externalResource: { internalOnly: true },
  group: { internalOnly: false },
  number: { internalOnly: false },
  person: { internalOnly: true },
  text: { internalOnly: false },
  textBlock: { internalOnly: false },
  textarea: { internalOnly: false },
};

const helpdeskVisibilityCondition = { condition: HelpdeskDestinationCondition.nullable().optional() };

export const HELPDESK_MAX_GROUP_ITEMS = 20;
export const HELPDESK_TITLE_TEMPLATE_MAX_LENGTH = 1_000;
export const HELPDESK_BODY_TEMPLATE_MAX_LENGTH = 20_000;

export const HelpdeskChoiceCardinality = z.enum(["single", "multiple"]);
export type HelpdeskChoiceCardinality = z.infer<typeof HelpdeskChoiceCardinality>;

export const HelpdeskChoicePresentation = z.enum(["list", "searchable"]);
export type HelpdeskChoicePresentation = z.infer<typeof HelpdeskChoicePresentation>;

export const HelpdeskChoiceOption = z.object({ id: NonEmptyString, label: NonEmptyString, semanticId: HelpdeskSemanticId.optional() });
export type HelpdeskChoiceOption = z.infer<typeof HelpdeskChoiceOption>;

export const HelpdeskLinearPriority = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export type HelpdeskLinearPriority = z.infer<typeof HelpdeskLinearPriority>;

export const HelpdeskChoiceLinearMappingScope = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("global") }).strict(),
  z.object({ kind: z.literal("team"), linearTeamId: NonEmptyString }).strict(),
]);
export type HelpdeskChoiceLinearMappingScope = z.infer<typeof HelpdeskChoiceLinearMappingScope>;

const HelpdeskChoiceLinearMappingBase = { scope: HelpdeskChoiceLinearMappingScope };
const HelpdeskChoiceLinearPriorityMapping = z
  .object({
    ...HelpdeskChoiceLinearMappingBase,
    property: z.literal("priority"),
    values: z.array(z.object({ optionId: NonEmptyString, value: HelpdeskLinearPriority.nullable() }).strict()),
  })
  .strict();
const HelpdeskChoiceLinearLabelMapping = z
  .object({
    ...HelpdeskChoiceLinearMappingBase,
    property: z.literal("label"),
    values: z.array(
      z
        .object({
          optionId: NonEmptyString,
          value: z.object({ labelId: NonEmptyString, labelGroupId: NonEmptyString.nullable() }).strict().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
const HelpdeskChoiceLinearWorkflowStateMapping = z
  .object({
    ...HelpdeskChoiceLinearMappingBase,
    property: z.literal("workflowState"),
    values: z.array(z.object({ optionId: NonEmptyString, value: NonEmptyString.nullable() }).strict()),
  })
  .strict();

export const HelpdeskChoiceLinearMapping = z.discriminatedUnion("property", [HelpdeskChoiceLinearPriorityMapping, HelpdeskChoiceLinearLabelMapping, HelpdeskChoiceLinearWorkflowStateMapping]);
export type HelpdeskChoiceLinearMapping = z.infer<typeof HelpdeskChoiceLinearMapping>;

const HelpdeskLinearMappingProperty = z.enum(["label", "priority", "workflowState"]);
const HelpdeskLinearMappingFieldLocation = { fieldId: NonEmptyString.optional() };
const HelpdeskLinearMappingChoiceLocation = { ...HelpdeskLinearMappingFieldLocation, mappingIndex: z.number().int().nonnegative() };

export const HelpdeskLinearMappingIssue = z.discriminatedUnion("code", [
  z.object({ code: z.literal("catalog_unavailable"), ...HelpdeskLinearMappingFieldLocation, property: HelpdeskLinearMappingProperty.optional() }).strict(),
  z.object({ code: z.literal("scope_unavailable"), ...HelpdeskLinearMappingChoiceLocation, property: HelpdeskLinearMappingProperty, scope: z.enum(["global", "team"]) }).strict(),
  z.object({ code: z.literal("label_unavailable"), ...HelpdeskLinearMappingChoiceLocation, optionIds: z.array(NonEmptyString).min(1), property: z.literal("label") }).strict(),
  z.object({ code: z.literal("workflow_state_unavailable"), ...HelpdeskLinearMappingChoiceLocation, optionIds: z.array(NonEmptyString).min(1), property: z.literal("workflowState") }).strict(),
  z
    .object({
      code: z.literal("conflicting_label_group"),
      ...HelpdeskLinearMappingFieldLocation,
      labelGroupName: NonEmptyString.nullable(),
      mappingIndex: z.number().int().nonnegative().optional(),
      optionIds: z.array(NonEmptyString),
      property: z.literal("label"),
      ruleIndexes: z.array(z.number().int().nonnegative()),
    })
    .strict(),
  z.object({ code: z.literal("source_unavailable"), fieldId: NonEmptyString, property: HelpdeskLinearMappingProperty, ruleIndex: z.number().int().nonnegative() }).strict(),
  z.object({ code: z.literal("target_unavailable"), property: z.enum(["assignee", "label", "release", "workflowState"]), ruleIndex: z.number().int().nonnegative() }).strict(),
]);
export type HelpdeskLinearMappingIssue = z.infer<typeof HelpdeskLinearMappingIssue>;

export const HelpdeskFormImportLinearMappingIssueGroup = z
  .object({
    departmentName: NonEmptyString,
    destinationSourceId: z.string().uuid(),
    issues: z.array(HelpdeskLinearMappingIssue).min(1),
    linearTeamName: NonEmptyString,
  })
  .strict();
export type HelpdeskFormImportLinearMappingIssueGroup = z.infer<typeof HelpdeskFormImportLinearMappingIssueGroup>;

const addChoiceLinearMappingErrors = (
  cardinality: HelpdeskChoiceCardinality,
  options: readonly HelpdeskChoiceOption[],
  mappings: readonly HelpdeskChoiceLinearMapping[] | undefined,
  path: readonly (string | number)[],
  ctx: z.RefinementCtx,
): void => {
  if (mappings === undefined) return;
  const optionIds = new Set(options.map((option) => option.id));
  const mappingKeys = new Set<string>();

  for (const [mappingIndex, mapping] of mappings.entries()) {
    const mappingPath = [...path, mappingIndex];
    const scopeKey = mapping.scope.kind === "global" ? "global" : `team:${mapping.scope.linearTeamId}`;
    const mappingKey = `${mapping.property}:${scopeKey}`;
    if (mappingKeys.has(mappingKey)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: mappingPath, message: "Linear mapping properties and scopes must be unique" });
    else mappingKeys.add(mappingKey);

    if (mapping.property === "workflowState" && mapping.scope.kind === "global") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...mappingPath, "scope"], message: "workflow-state mappings require a team scope" });
    }
    if (cardinality === "multiple" && mapping.property !== "label") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...mappingPath, "property"], message: "multiple-choice fields support only label mappings" });
    }

    const mappedOptionIds = new Set<string>();
    for (const [valueIndex, value] of mapping.values.entries()) {
      if (!optionIds.has(value.optionId))
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...mappingPath, "values", valueIndex, "optionId"], message: "mapped option must exist on the choice field" });
      if (mappedOptionIds.has(value.optionId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...mappingPath, "values", valueIndex, "optionId"], message: "each choice option must appear exactly once in a Linear mapping" });
      } else mappedOptionIds.add(value.optionId);
    }
    if (mappedOptionIds.size !== optionIds.size || [...optionIds].some((optionId) => !mappedOptionIds.has(optionId))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...mappingPath, "values"], message: "a Linear mapping must contain exactly one value for every choice option" });
    }
  }
};

const addChoiceOptionSemanticIdErrors = (options: readonly HelpdeskChoiceOption[], path: readonly (string | number)[], ctx: z.RefinementCtx): void => {
  const seen = new Set<string>();
  for (const [index, option] of options.entries()) {
    if (option.semanticId === undefined || !seen.has(option.semanticId)) {
      if (option.semanticId !== undefined) seen.add(option.semanticId);
      continue;
    }
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "semanticId"], message: "choice option semantic IDs must be unique" });
  }
};

export const HelpdeskPersonCardinality = z.enum(["single", "multiple"]);
export type HelpdeskPersonCardinality = z.infer<typeof HelpdeskPersonCardinality>;

export const HelpdeskPersonGroupMatch = z.enum(["any", "all"]);
export type HelpdeskPersonGroupMatch = z.infer<typeof HelpdeskPersonGroupMatch>;

export const HelpdeskPersonDirectoryFilter = z
  .object({
    groups: z.array(EntraGroupRef).min(1).max(20),
    match: HelpdeskPersonGroupMatch,
  })
  .strict()
  .superRefine((filter, ctx) => {
    if (new Set(filter.groups.map((group) => group.id)).size !== filter.groups.length) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["groups"], message: "groups must be unique" });
  });
export type HelpdeskPersonDirectoryFilter = z.infer<typeof HelpdeskPersonDirectoryFilter>;

export const HelpdeskTemporalCalendarConstraint = z.union([
  z.literal("past"),
  z.literal("future"),
  z.object({ kind: z.literal("recent"), amount: z.number().int().min(1).max(999), unit: z.enum(["days", "months"]) }),
  z.object({ kind: z.literal("future"), amount: z.number().int().min(1).max(999), unit: z.enum(["hours", "days"]) }),
]);
export type HelpdeskTemporalCalendarConstraint = z.infer<typeof HelpdeskTemporalCalendarConstraint>;

export const HelpdeskTemporalTimeWindow = z
  .object({ start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) })
  .refine((window) => window.start !== window.end, { message: "start and end must differ", path: ["end"] });
export type HelpdeskTemporalTimeWindow = z.infer<typeof HelpdeskTemporalTimeWindow>;

export const HelpdeskTemporalBounds = z
  .object({ calendar: HelpdeskTemporalCalendarConstraint.optional(), timeWindow: HelpdeskTemporalTimeWindow.optional() })
  .refine((bounds) => bounds.calendar !== undefined || bounds.timeWindow !== undefined, { message: "at least one temporal bound is required" });
export type HelpdeskTemporalBounds = z.infer<typeof HelpdeskTemporalBounds>;

const HelpdeskDateTemporalBounds = HelpdeskTemporalBounds.superRefine((bounds, ctx) => {
  if (bounds.timeWindow !== undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["timeWindow"], message: "date fields cannot constrain time" });
  if (typeof bounds.calendar === "object" && bounds.calendar.kind === "future" && bounds.calendar.unit === "hours")
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["calendar", "unit"], message: "date fields cannot constrain hours" });
});

export const HelpdeskTemporalRelations = z.object({ beforeFieldId: z.string().uuid().optional(), afterFieldId: z.string().uuid().optional() });
export type HelpdeskTemporalRelations = z.infer<typeof HelpdeskTemporalRelations>;

const temporalDateField = { temporalBounds: HelpdeskDateTemporalBounds.optional() };
const temporalDateTimeField = { temporalBounds: HelpdeskTemporalBounds.optional(), temporalRelations: HelpdeskTemporalRelations.optional() };

const HelpdeskFormFieldBase = {
  id: z.string().uuid(),
  label: NonEmptyString,
  sortOrder: z.number().int().nonnegative(),
  helpText: NonEmptyString.optional(),
  semanticId: HelpdeskSemanticId.optional(),
  control: z.never().optional(),
};

const answerableField = { ...HelpdeskFormFieldBase, required: z.boolean() };

const numberConstraints = {
  min: z.number().finite().optional(),
  max: z.number().finite().optional(),
  step: z.number().finite().positive().optional(),
};

export const HELPDESK_MAX_ATTACHMENT_REFERENCES = LINEAR_CONVERSATION_MAX_ATTACHMENTS;
export const HELPDESK_MAX_ATTACHMENT_FILE_BYTES = LINEAR_CONVERSATION_MAX_ATTACHMENT_BYTES;
export const HELPDESK_MAX_ATTACHMENT_REQUEST_BYTES = LINEAR_CONVERSATION_MAX_REQUEST_BYTES;
export const HELPDESK_ATTACHMENT_FORM_PART_PREFIX = "attachment:";
export const HELPDESK_ATTACHMENT_COMMENT_PART = "attachment";
export const HELPDESK_SUBMISSION_PAYLOAD_PART = "payload";
export const HELPDESK_IDEMPOTENCY_HEADER = "Idempotency-Key";

export const HelpdeskAttachmentFileName = LinearConversationAttachmentFileName;
export type HelpdeskAttachmentFileName = z.infer<typeof HelpdeskAttachmentFileName>;

export const HelpdeskSubmittedAttachment = z.object({
  fileName: HelpdeskAttachmentFileName,
  sizeBytes: z.number().int().positive().max(HELPDESK_MAX_ATTACHMENT_FILE_BYTES),
});
export type HelpdeskSubmittedAttachment = z.infer<typeof HelpdeskSubmittedAttachment>;

const HelpdeskAttachmentPortalUrl = z.string().regex(/^\/api\/helpdesk\/requests\/[^/]+\/targets\/[^/]+\/assets\/[^/]+$/);

export const HelpdeskAttachmentInlineMedia = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("image"), url: HelpdeskAttachmentPortalUrl }),
  z.object({ kind: z.literal("video"), url: HelpdeskAttachmentPortalUrl }),
  z.object({ kind: z.literal("none") }),
]);
export type HelpdeskAttachmentInlineMedia = z.infer<typeof HelpdeskAttachmentInlineMedia>;

export const HelpdeskAttachmentMedia = z.object({
  id: NonEmptyString,
  fileName: HelpdeskAttachmentFileName,
  contentType: NonEmptyString.max(255).optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  downloadUrl: HelpdeskAttachmentPortalUrl,
  inline: HelpdeskAttachmentInlineMedia,
});
export type HelpdeskAttachmentMedia = z.infer<typeof HelpdeskAttachmentMedia>;

const HelpdeskBasicField = z.object({
  ...answerableField,
  ...helpdeskVisibilityCondition,
  type: HelpdeskFormFieldType.extract(["text", "textarea"]),
  required: z.boolean(),
  options: z.never().optional(),
  content: z.never().optional(),
});
const HelpdeskDateField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("date"), ...temporalDateField });
const HelpdeskTextBlockField = z.object({
  ...HelpdeskFormFieldBase,
  ...helpdeskVisibilityCondition,
  type: HelpdeskFormFieldType.extract(["textBlock"]),
  required: z.literal(false),
  options: z.never().optional(),
  content: NonEmptyString,
});
const HelpdeskChoiceFieldBase = z
  .object({
    ...answerableField,
    ...helpdeskVisibilityCondition,
    type: z.literal("choice"),
    cardinality: HelpdeskChoiceCardinality,
    presentation: HelpdeskChoicePresentation,
    options: z.array(HelpdeskChoiceOption).min(1),
  })
  .strict();
const HelpdeskChoiceField = HelpdeskChoiceFieldBase.extend({ linearMappings: z.array(HelpdeskChoiceLinearMapping).optional() });
const HelpdeskPersonField = z.object({
  ...answerableField,
  ...helpdeskVisibilityCondition,
  type: z.literal("person"),
  cardinality: HelpdeskPersonCardinality,
  directoryFilter: HelpdeskPersonDirectoryFilter.optional(),
});
const HelpdeskEmailField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("email") });
const HelpdeskNumberField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("number"), ...numberConstraints });
const HelpdeskDateTimeField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("dateTime"), ...temporalDateTimeField });
const HelpdeskConfirmationField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("confirmation") });
const HelpdeskAttachmentField = z.object({ ...answerableField, ...helpdeskVisibilityCondition, type: z.literal("attachment"), multiple: z.boolean() });
const HelpdeskExternalResourceField = z.object({
  ...answerableField,
  ...helpdeskVisibilityCondition,
  type: z.literal("externalResource"),
  source: HelpdeskExternalResourceSource,
  semantic: HelpdeskExternalResourceSemantic.optional(),
  control: HelpdeskPluginComponentControl.optional(),
});

const rejectManagedKind = (value: unknown): unknown => (typeof value === "object" && value !== null && "kind" in value ? undefined : value);

const HelpdeskGroupChildCondition = HelpdeskDestinationCondition.nullable().optional();
const HelpdeskGroupChildBasicField = HelpdeskBasicField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildDateField = HelpdeskDateField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildTextBlockField = HelpdeskTextBlockField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildChoiceField = HelpdeskChoiceFieldBase.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildPersonField = HelpdeskPersonField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildEmailField = HelpdeskEmailField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildNumberField = HelpdeskNumberField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildDateTimeField = HelpdeskDateTimeField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildConfirmationField = HelpdeskConfirmationField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildExternalResourceField = HelpdeskExternalResourceField.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });

const helpdeskGroupChildFieldVariants = [
  HelpdeskGroupChildBasicField,
  HelpdeskGroupChildDateField,
  HelpdeskGroupChildTextBlockField,
  HelpdeskGroupChildChoiceField,
  HelpdeskGroupChildPersonField,
  HelpdeskGroupChildEmailField,
  HelpdeskGroupChildNumberField,
  HelpdeskGroupChildDateTimeField,
  HelpdeskGroupChildConfirmationField,
  HelpdeskGroupChildExternalResourceField,
] as const;
const HelpdeskGroupChildField = z.discriminatedUnion("type", helpdeskGroupChildFieldVariants);

const helpdeskTopLevelFieldVariants = [
  HelpdeskBasicField,
  HelpdeskDateField,
  HelpdeskTextBlockField,
  HelpdeskChoiceField,
  HelpdeskPersonField,
  HelpdeskEmailField,
  HelpdeskNumberField,
  HelpdeskDateTimeField,
  HelpdeskConfirmationField,
  HelpdeskAttachmentField,
  HelpdeskExternalResourceField,
] as const;
const HelpdeskTopLevelFieldSchema = z.discriminatedUnion("type", helpdeskTopLevelFieldVariants);

type HelpdeskTopLevelField = z.infer<typeof HelpdeskTopLevelFieldSchema>;
type HelpdeskResolvedGroupField = {
  id: string;
  label: string;
  sortOrder: number;
  helpText?: string;
  semanticId?: HelpdeskSemanticId;
  condition?: HelpdeskDestinationCondition | null;
  type: "group";
  fields: HelpdeskResolvedGroupChildField[];
  itemTemplate: string | null;
  minItems: number;
  maxItems: number;
  repeatable: boolean;
};
type HelpdeskResolvedGroupChildField = Exclude<HelpdeskTopLevelField, { type: "attachment" }> | HelpdeskResolvedGroupField;
export type HelpdeskFormField = HelpdeskTopLevelField | HelpdeskResolvedGroupField;

const HelpdeskFormGroup: z.ZodType<HelpdeskResolvedGroupField, z.ZodTypeDef, unknown> = z
  .object({
    ...HelpdeskFormFieldBase,
    ...helpdeskVisibilityCondition,
    type: z.literal("group"),
    fields: z.array(z.lazy(() => z.union([HelpdeskGroupChildField, HelpdeskFormGroup]))).min(1),
    itemTemplate: z.string().trim().min(1).max(10_000).nullable(),
    minItems: z.number().int().min(0).max(HELPDESK_MAX_GROUP_ITEMS),
    maxItems: z.number().int().min(1).max(HELPDESK_MAX_GROUP_ITEMS),
    repeatable: z.boolean(),
  })
  .superRefine((group, ctx) => {
    if (group.minItems > group.maxItems) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["minItems"], message: "minItems must not exceed maxItems" });
    if (!group.repeatable && group.maxItems !== 1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["maxItems"], message: "non-repeatable groups must allow exactly one item" });
    for (const [index, field] of group.fields.entries()) {
      if ("kind" in field) continue;
      if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "min"], message: "min must not exceed max" });
      if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["fields", index, "options"], ctx);
    }
  });

const HelpdeskFormFieldSchema = z.union([HelpdeskTopLevelFieldSchema, HelpdeskFormGroup]);
export const HelpdeskFormField = HelpdeskFormFieldSchema.superRefine((field, ctx) => {
  if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["min"], message: "min must not exceed max" });
  }
  if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["options"], ctx);
  if (field.type === "choice") addChoiceLinearMappingErrors(field.cardinality, field.options, field.linearMappings, ["linearMappings"], ctx);
});

export type HelpdeskSemanticIdError =
  | { code: "invalid"; fieldId: string; semanticId: string }
  | { code: "duplicate"; fieldId: string; semanticId: string }
  | { code: "group_child_requires_group"; fieldId: string; semanticId: string };

export const helpdeskSemanticIdForField = (field: HelpdeskFormField): string | undefined => field.semanticId;

export const composeHelpdeskSemanticId = (groupSemanticId: string | undefined, fieldSemanticId: string | undefined): string | undefined =>
  fieldSemanticId === undefined ? undefined : groupSemanticId === undefined ? fieldSemanticId : `${groupSemanticId}.${fieldSemanticId}`;

export const helpdeskSemanticIdErrors = (fields: readonly HelpdeskFormField[]): HelpdeskSemanticIdError[] => {
  const seen = new Set<string>();
  const errors: HelpdeskSemanticIdError[] = [];
  const add = (fieldId: string, semanticId: string): void => {
    if (!HelpdeskSemanticId.safeParse(semanticId).success) errors.push({ code: "invalid", fieldId, semanticId });
    if (seen.has(semanticId)) errors.push({ code: "duplicate", fieldId, semanticId });
    else seen.add(semanticId);
  };

  const visit = (scopeFields: readonly HelpdeskFormField[], parentSemanticId: string | undefined, insideGroup: boolean): void => {
    for (const field of scopeFields) {
      const fieldSemanticId = helpdeskSemanticIdForField(field);
      const composedSemanticId = fieldSemanticId === undefined ? undefined : composeHelpdeskSemanticId(parentSemanticId, fieldSemanticId);
      if (fieldSemanticId !== undefined) {
        if (insideGroup && parentSemanticId === undefined) {
          errors.push({ code: "group_child_requires_group", fieldId: field.id, semanticId: fieldSemanticId });
          add(field.id, fieldSemanticId);
        } else if (composedSemanticId !== undefined) add(field.id, composedSemanticId);
      }
      if (field.type === "group") visit(field.fields, composedSemanticId, true);
    }
  };

  visit(fields, undefined, false);
  return errors;
};

const HelpdeskFormFieldInputBase = {
  id: z.string().uuid().optional(),
  label: NonEmptyString,
  sortOrder: z.number().int().nonnegative(),
  helpText: NonEmptyString.optional(),
  semanticId: HelpdeskSemanticId.optional(),
  control: z.never().optional(),
};

const answerableFieldInput = { ...HelpdeskFormFieldInputBase, required: z.boolean() };

const HelpdeskBasicFieldInput = z.object({
  ...answerableFieldInput,
  ...helpdeskVisibilityCondition,
  type: HelpdeskFormFieldType.extract(["text", "textarea"]),
  required: z.boolean(),
  options: z.never().optional(),
  content: z.never().optional(),
});
const HelpdeskDateFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("date"), ...temporalDateField });
const HelpdeskTextBlockFieldInput = z.object({
  ...HelpdeskFormFieldInputBase,
  ...helpdeskVisibilityCondition,
  type: HelpdeskFormFieldType.extract(["textBlock"]),
  required: z.literal(false),
  options: z.never().optional(),
  content: NonEmptyString,
});
const HelpdeskChoiceFieldInputBase = z
  .object({
    ...answerableFieldInput,
    ...helpdeskVisibilityCondition,
    type: z.literal("choice"),
    cardinality: HelpdeskChoiceCardinality,
    presentation: HelpdeskChoicePresentation,
    options: z.array(HelpdeskChoiceOption).min(1),
  })
  .strict();
const HelpdeskChoiceFieldInput = HelpdeskChoiceFieldInputBase.extend({ linearMappings: z.array(HelpdeskChoiceLinearMapping).optional() });
const HelpdeskPersonFieldInput = z.object({
  ...answerableFieldInput,
  ...helpdeskVisibilityCondition,
  type: z.literal("person"),
  cardinality: HelpdeskPersonCardinality,
  directoryFilter: HelpdeskPersonDirectoryFilter.optional(),
});
const HelpdeskEmailFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("email") });
const HelpdeskNumberFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("number"), ...numberConstraints });
const HelpdeskDateTimeFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("dateTime"), ...temporalDateTimeField });
const HelpdeskConfirmationFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("confirmation") });
const HelpdeskAttachmentFieldInput = z.object({ ...answerableFieldInput, ...helpdeskVisibilityCondition, type: z.literal("attachment"), multiple: z.boolean() });
const HelpdeskExternalResourceFieldInput = z.object({
  ...answerableFieldInput,
  ...helpdeskVisibilityCondition,
  type: z.literal("externalResource"),
  source: HelpdeskExternalResourceSource,
  semantic: HelpdeskExternalResourceSemantic.optional(),
  control: HelpdeskPluginComponentControl.optional(),
});

const HelpdeskGroupChildBasicFieldInput = HelpdeskBasicFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildDateFieldInput = HelpdeskDateFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildTextBlockFieldInput = HelpdeskTextBlockFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildChoiceFieldInput = HelpdeskChoiceFieldInputBase.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildPersonFieldInput = HelpdeskPersonFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildEmailFieldInput = HelpdeskEmailFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildNumberFieldInput = HelpdeskNumberFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildDateTimeFieldInput = HelpdeskDateTimeFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildConfirmationFieldInput = HelpdeskConfirmationFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });
const HelpdeskGroupChildExternalResourceFieldInput = HelpdeskExternalResourceFieldInput.omit({ condition: true }).extend({ condition: HelpdeskGroupChildCondition });

const helpdeskGroupChildFieldInputVariants = [
  HelpdeskGroupChildBasicFieldInput,
  HelpdeskGroupChildDateFieldInput,
  HelpdeskGroupChildTextBlockFieldInput,
  HelpdeskGroupChildChoiceFieldInput,
  HelpdeskGroupChildPersonFieldInput,
  HelpdeskGroupChildEmailFieldInput,
  HelpdeskGroupChildNumberFieldInput,
  HelpdeskGroupChildDateTimeFieldInput,
  HelpdeskGroupChildConfirmationFieldInput,
  HelpdeskGroupChildExternalResourceFieldInput,
] as const;
const HelpdeskGroupChildFieldInput = z.discriminatedUnion("type", helpdeskGroupChildFieldInputVariants);
const HelpdeskGroupChildEntryInput = z.lazy(() => z.union([z.preprocess(rejectManagedKind, HelpdeskGroupChildFieldInput), HelpdeskManagedGroupChildReferenceInput]));

const helpdeskTopLevelFieldInputVariants = [
  HelpdeskBasicFieldInput,
  HelpdeskDateFieldInput,
  HelpdeskTextBlockFieldInput,
  HelpdeskChoiceFieldInput,
  HelpdeskPersonFieldInput,
  HelpdeskEmailFieldInput,
  HelpdeskNumberFieldInput,
  HelpdeskDateTimeFieldInput,
  HelpdeskConfirmationFieldInput,
  HelpdeskAttachmentFieldInput,
  HelpdeskExternalResourceFieldInput,
] as const;

const HelpdeskFormGroupInput = z
  .object({
    ...HelpdeskFormFieldInputBase,
    ...helpdeskVisibilityCondition,
    type: z.literal("group"),
    fields: z.array(HelpdeskGroupChildEntryInput).min(1),
    itemTemplate: z.string().trim().min(1).max(10_000).nullable(),
    minItems: z.number().int().min(0).max(HELPDESK_MAX_GROUP_ITEMS),
    maxItems: z.number().int().min(1).max(HELPDESK_MAX_GROUP_ITEMS),
    repeatable: z.boolean(),
  })
  .superRefine((group, ctx) => {
    if (group.minItems > group.maxItems) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["minItems"], message: "minItems must not exceed maxItems" });
    if (!group.repeatable && group.maxItems !== 1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["maxItems"], message: "non-repeatable groups must allow exactly one item" });
    for (const [index, field] of group.fields.entries()) {
      if ("kind" in field) continue;
      if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "min"], message: "min must not exceed max" });
      if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["fields", index, "options"], ctx);
    }
  });

const HelpdeskFormFieldInputSchema = z.union([z.discriminatedUnion("type", helpdeskTopLevelFieldInputVariants), HelpdeskFormGroupInput]);
export const HelpdeskFormFieldInput = HelpdeskFormFieldInputSchema.superRefine((field, ctx) => {
  if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["min"], message: "min must not exceed max" });
  }
  if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["options"], ctx);
  if (field.type === "choice") addChoiceLinearMappingErrors(field.cardinality, field.options, field.linearMappings, ["linearMappings"], ctx);
});
export type HelpdeskFormFieldInput = z.infer<typeof HelpdeskFormFieldInput>;

export const HelpdeskManagedFieldType = HelpdeskFormFieldType.exclude(["textBlock"]);
export type HelpdeskManagedFieldType = z.infer<typeof HelpdeskManagedFieldType>;

const HelpdeskManagedFieldDefaults = z
  .object({
    label: NonEmptyString,
    helpText: NonEmptyString.optional(),
    required: z.boolean(),
  })
  .strict();
const managedDefinitionPresentation = { defaults: HelpdeskManagedFieldDefaults.optional(), semanticId: HelpdeskSemanticId.optional(), control: z.never().optional() };

const HelpdeskManagedSimpleDefinition = z.object({ ...managedDefinitionPresentation, type: HelpdeskManagedFieldType.extract(["text", "textarea", "email", "confirmation"]) });
const HelpdeskManagedDateDefinition = z.object({ ...managedDefinitionPresentation, type: z.literal("date"), ...temporalDateField });
const HelpdeskManagedDateTimeDefinition = z.object({ ...managedDefinitionPresentation, type: z.literal("dateTime"), temporalBounds: HelpdeskTemporalBounds.optional() });
const HelpdeskManagedExternalResourceDefinition = z.object({
  ...managedDefinitionPresentation,
  type: z.literal("externalResource"),
  source: HelpdeskExternalResourceSource,
  semantic: HelpdeskExternalResourceSemantic.optional(),
  control: HelpdeskPluginComponentControl.optional(),
});
// Managed definitions own reusable bounds. Relationships belong to a particular
// form reference, so group children must not persist them in the definition.
const HelpdeskManagedGroupChildBasicField = HelpdeskGroupChildBasicField;
const HelpdeskManagedGroupChildDateField = HelpdeskGroupChildDateField;
const HelpdeskManagedGroupChildTextBlockField = HelpdeskGroupChildTextBlockField;
const HelpdeskManagedGroupChildChoiceField = HelpdeskGroupChildChoiceField;
const HelpdeskManagedGroupChildPersonField = HelpdeskGroupChildPersonField;
const HelpdeskManagedGroupChildEmailField = HelpdeskGroupChildEmailField;
const HelpdeskManagedGroupChildNumberField = HelpdeskGroupChildNumberField;
const HelpdeskManagedGroupChildDateTimeField = z
  .object({ ...answerableField, type: z.literal("dateTime"), temporalBounds: HelpdeskTemporalBounds.optional(), condition: HelpdeskGroupChildCondition })
  .strict();
const helpdeskManagedGroupChildFieldVariants = [
  HelpdeskManagedGroupChildBasicField,
  HelpdeskManagedGroupChildDateField,
  HelpdeskManagedGroupChildTextBlockField,
  HelpdeskManagedGroupChildChoiceField,
  HelpdeskManagedGroupChildPersonField,
  HelpdeskManagedGroupChildEmailField,
  HelpdeskManagedGroupChildNumberField,
  HelpdeskManagedGroupChildDateTimeField,
  HelpdeskGroupChildConfirmationField,
  HelpdeskGroupChildExternalResourceField,
] as const;
const HelpdeskManagedGroupChildField = z.preprocess(rejectManagedKind, z.discriminatedUnion("type", helpdeskManagedGroupChildFieldVariants));
const HelpdeskManagedChoiceDefinition = z
  .object({
    ...managedDefinitionPresentation,
    type: z.literal("choice"),
    cardinality: HelpdeskChoiceCardinality,
    options: z.array(HelpdeskChoiceOption).min(1),
    linearMappings: z.array(HelpdeskChoiceLinearMapping).optional(),
  })
  .strict();
const HelpdeskManagedPersonDefinition = z.object({
  ...managedDefinitionPresentation,
  type: z.literal("person"),
  cardinality: HelpdeskPersonCardinality,
  directoryFilter: HelpdeskPersonDirectoryFilter.optional(),
});
const HelpdeskManagedNumberDefinition = z.object({ ...managedDefinitionPresentation, type: z.literal("number"), ...numberConstraints });
const HelpdeskManagedAttachmentDefinition = z.object({ ...managedDefinitionPresentation, type: z.literal("attachment"), multiple: z.boolean() });
const HelpdeskManagedGroupDefinition = z.object({
  ...managedDefinitionPresentation,
  type: z.literal("group"),
  fields: z.array(HelpdeskManagedGroupChildField).min(1),
  itemTemplate: z.string().trim().min(1).max(10_000).nullable(),
});

const HelpdeskManagedFieldDefinitionSchema = z.discriminatedUnion("type", [
  HelpdeskManagedSimpleDefinition,
  HelpdeskManagedDateDefinition,
  HelpdeskManagedDateTimeDefinition,
  HelpdeskManagedExternalResourceDefinition,
  HelpdeskManagedChoiceDefinition,
  HelpdeskManagedPersonDefinition,
  HelpdeskManagedNumberDefinition,
  HelpdeskManagedAttachmentDefinition,
  HelpdeskManagedGroupDefinition,
]);

export const HelpdeskManagedFieldDefinition = z.preprocess(rejectManagedKind, HelpdeskManagedFieldDefinitionSchema).superRefine((definition, ctx) => {
  if (definition.type === "number" && definition.min !== undefined && definition.max !== undefined && definition.min > definition.max)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["min"], message: "min must not exceed max" });
  if (definition.type === "choice") addChoiceOptionSemanticIdErrors(definition.options, ["options"], ctx);
  if (definition.type === "choice") addChoiceLinearMappingErrors(definition.cardinality, definition.options, definition.linearMappings, ["linearMappings"], ctx);
  if (definition.type === "group") {
    const precedingChildIds = new Set<string>();
    for (const [index, field] of definition.fields.entries()) {
      if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["fields", index, "options"], ctx);
      if (field.condition !== undefined && field.condition !== null) {
        for (const conditionGroup of [...field.condition.matchAnyOf, ...field.condition.excludeAnyOf]) {
          for (const predicate of conditionGroup.predicates) {
            if (predicate.groupId !== undefined || !precedingChildIds.has(predicate.fieldId)) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "condition"], message: "group child conditions may target only preceding siblings" });
            }
          }
        }
      }
      precedingChildIds.add(field.id);
    }
  }
});
export type HelpdeskManagedFieldDefinition = z.infer<typeof HelpdeskManagedFieldDefinition>;

const HelpdeskManagedFieldReferenceOverrides = z
  .object({
    label: NonEmptyString.optional(),
    helpText: NonEmptyString.nullable().optional(),
    required: z.boolean().optional(),
  })
  .strict();

const managedReferenceBase = {
  kind: z.literal("managed"),
  id: z.string().uuid(),
  managedFieldId: z.string().uuid(),
  label: NonEmptyString,
  helpText: NonEmptyString.optional(),
  required: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  overrides: HelpdeskManagedFieldReferenceOverrides.optional(),
  control: z.never().optional(),
  ...helpdeskVisibilityCondition,
};
const managedReferenceInputBase = { ...managedReferenceBase, id: z.string().uuid().optional() };

const refineManagedGroupReference = (reference: { minItems?: number; maxItems?: number; repeatable?: boolean }, ctx: z.RefinementCtx): void => {
  if (reference.minItems !== undefined && reference.maxItems !== undefined && reference.minItems > reference.maxItems)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["minItems"], message: "minItems must not exceed maxItems" });
  if (reference.repeatable === false && reference.maxItems !== undefined && reference.maxItems !== 1)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["maxItems"], message: "non-repeatable groups must allow exactly one item" });
};

export const HelpdeskManagedFieldReference = z.union([
  z.object({ ...managedReferenceBase, type: HelpdeskManagedFieldType.extract(["text", "textarea", "date", "email", "number", "confirmation"]) }),
  z.object({ ...managedReferenceBase, type: z.literal("person"), cardinality: HelpdeskPersonCardinality.optional() }),
  z.object({ ...managedReferenceBase, type: z.literal("attachment"), multiple: z.boolean().optional() }),
  z.object({ ...managedReferenceBase, type: z.literal("dateTime"), temporalRelations: HelpdeskTemporalRelations.optional() }),
  z.object({ ...managedReferenceBase, type: z.literal("choice"), cardinality: HelpdeskChoiceCardinality.optional(), presentation: HelpdeskChoicePresentation }),
  z.object({ ...managedReferenceBase, type: z.literal("externalResource"), control: z.never().optional() }),
  z
    .object({
      ...managedReferenceBase,
      type: z.literal("group"),
      repeatable: z.boolean(),
      minItems: z.number().int().min(0).max(HELPDESK_MAX_GROUP_ITEMS).optional(),
      maxItems: z.number().int().min(1).max(HELPDESK_MAX_GROUP_ITEMS),
      temporalRelationsByFieldId: z.record(z.string().uuid(), HelpdeskTemporalRelations).optional(),
    })
    .superRefine(refineManagedGroupReference),
]);
export type HelpdeskManagedFieldReference = z.infer<typeof HelpdeskManagedFieldReference>;
export const HelpdeskManagedFieldReferenceInput = z.union([
  z.object({ ...managedReferenceInputBase, type: HelpdeskManagedFieldType.extract(["text", "textarea", "date", "email", "number", "confirmation"]) }),
  z.object({ ...managedReferenceInputBase, type: z.literal("person"), cardinality: HelpdeskPersonCardinality.optional() }),
  z.object({ ...managedReferenceInputBase, type: z.literal("attachment"), multiple: z.boolean().optional() }),
  z.object({ ...managedReferenceInputBase, type: z.literal("dateTime"), temporalRelations: HelpdeskTemporalRelations.optional() }),
  z.object({ ...managedReferenceInputBase, type: z.literal("choice"), cardinality: HelpdeskChoiceCardinality.optional(), presentation: HelpdeskChoicePresentation }),
  z.object({ ...managedReferenceInputBase, type: z.literal("externalResource"), control: z.never().optional() }),
  z
    .object({
      ...managedReferenceInputBase,
      type: z.literal("group"),
      repeatable: z.boolean(),
      minItems: z.number().int().min(0).max(HELPDESK_MAX_GROUP_ITEMS).optional(),
      maxItems: z.number().int().min(1).max(HELPDESK_MAX_GROUP_ITEMS),
      temporalRelationsByFieldId: z.record(z.string().uuid(), HelpdeskTemporalRelations).optional(),
    })
    .superRefine(refineManagedGroupReference),
]);
export type HelpdeskManagedFieldReferenceInput = z.infer<typeof HelpdeskManagedFieldReferenceInput>;

const HelpdeskManagedGroupChildReference = HelpdeskManagedFieldReference.superRefine((reference, ctx) => {
  if (reference.type === "attachment") ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["type"], message: "attachments are not valid group children" });
});
const HelpdeskManagedGroupChildReferenceInput = HelpdeskManagedFieldReferenceInput.superRefine((reference, ctx) => {
  if (reference.type === "attachment") ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["type"], message: "attachments are not valid group children" });
});

const HelpdeskAuthoredGroupChildEntry = z.lazy(() => z.union([z.preprocess(rejectManagedKind, HelpdeskGroupChildField), HelpdeskManagedGroupChildReference]));
const HelpdeskAuthoredFormGroup = z
  .object({
    ...HelpdeskFormFieldBase,
    ...helpdeskVisibilityCondition,
    type: z.literal("group"),
    fields: z.array(HelpdeskAuthoredGroupChildEntry).min(1),
    itemTemplate: z.string().trim().min(1).max(10_000).nullable(),
    minItems: z.number().int().min(0).max(HELPDESK_MAX_GROUP_ITEMS),
    maxItems: z.number().int().min(1).max(HELPDESK_MAX_GROUP_ITEMS),
    repeatable: z.boolean(),
  })
  .superRefine((group, ctx) => {
    if (group.minItems > group.maxItems) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["minItems"], message: "minItems must not exceed maxItems" });
    if (!group.repeatable && group.maxItems !== 1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["maxItems"], message: "non-repeatable groups must allow exactly one item" });
    for (const [index, field] of group.fields.entries()) {
      if ("kind" in field) continue;
      if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", index, "min"], message: "min must not exceed max" });
      if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["fields", index, "options"], ctx);
    }
  });
const HelpdeskAuthoredFormField = z.union([HelpdeskTopLevelFieldSchema, HelpdeskAuthoredFormGroup]).superRefine((field, ctx) => {
  if (field.type === "number" && field.min !== undefined && field.max !== undefined && field.min > field.max) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["min"], message: "min must not exceed max" });
  }
  if (field.type === "choice") addChoiceOptionSemanticIdErrors(field.options, ["options"], ctx);
  if (field.type === "choice") addChoiceLinearMappingErrors(field.cardinality, field.options, field.linearMappings, ["linearMappings"], ctx);
});

const HelpdeskInlineFormField = z.preprocess(rejectManagedKind, HelpdeskAuthoredFormField);
const HelpdeskInlineFormFieldInput = z.preprocess(rejectManagedKind, HelpdeskFormFieldInput);

export const HelpdeskFormFieldEntry = z.union([HelpdeskManagedFieldReference, HelpdeskInlineFormField]);
export type HelpdeskFormFieldEntry = z.infer<typeof HelpdeskFormFieldEntry>;
export const HelpdeskFormFieldEntryInput = z.union([HelpdeskManagedFieldReferenceInput, HelpdeskInlineFormFieldInput]);
export type HelpdeskFormFieldEntryInput = z.infer<typeof HelpdeskFormFieldEntryInput>;

export const HelpdeskFormScalarValue = z.union([z.string(), z.array(z.string()), z.number().finite(), z.boolean(), HelpdeskExternalResourceBrowser, HelpdeskExternalResourceMaterialized]);
export type HelpdeskFormScalarValue = z.infer<typeof HelpdeskFormScalarValue>;

export type HelpdeskFormGroupValue = Array<Record<string, HelpdeskFormValue>>;
export type HelpdeskFormValue = HelpdeskFormScalarValue | HelpdeskFormGroupValue;

const HelpdeskFormValueSchema: z.ZodType<HelpdeskFormValue> = z.lazy(() => z.union([HelpdeskFormScalarValue, HelpdeskFormGroupValue]));
export const HelpdeskFormGroupValue: z.ZodType<HelpdeskFormGroupValue> = z.array(z.record(z.string().uuid(), HelpdeskFormValueSchema)).max(HELPDESK_MAX_GROUP_ITEMS);
export const HelpdeskFormValue: z.ZodType<HelpdeskFormValue> = z.union([HelpdeskFormScalarValue, HelpdeskFormGroupValue]);

// Request input only: connected apps may send typed external-resource values, which are materialized before any output type sees them.
export type HelpdeskFormInputValue = HelpdeskFormScalarValue | HelpdeskExternalResourceTypedValue | Array<Record<string, HelpdeskFormInputValue>>;
export const HelpdeskFormInputValue: z.ZodType<HelpdeskFormInputValue> = z.lazy(() =>
  z.union([HelpdeskFormScalarValue, HelpdeskExternalResourceTypedValue, z.array(z.record(z.string().uuid(), HelpdeskFormInputValue)).max(HELPDESK_MAX_GROUP_ITEMS)]),
);

export const HelpdeskWebhookAttachmentValue = z.union([HelpdeskAttachmentFileName, z.array(HelpdeskAttachmentFileName).min(1).max(HELPDESK_MAX_ATTACHMENT_REFERENCES)]);
export type HelpdeskWebhookAttachmentValue = z.infer<typeof HelpdeskWebhookAttachmentValue>;

export const HelpdeskWebhookAttachmentAnswer = z.object({ fieldId: z.string().uuid(), label: z.string(), value: HelpdeskWebhookAttachmentValue });
export type HelpdeskWebhookAttachmentAnswer = z.infer<typeof HelpdeskWebhookAttachmentAnswer>;

export const HelpdeskResolvedPerson = z.object({ kind: z.literal("person"), entraId: NonEmptyString, displayName: NonEmptyString, email: z.string().email() });
export type HelpdeskResolvedPerson = z.infer<typeof HelpdeskResolvedPerson>;

export const HelpdeskWebhookLeafValue = z.union([HelpdeskFormScalarValue, HelpdeskResolvedPerson, z.array(HelpdeskResolvedPerson)]);
export type HelpdeskWebhookLeafValue = z.infer<typeof HelpdeskWebhookLeafValue>;

export const HelpdeskWebhookChoiceSelection = z.object({ value: NonEmptyString, semanticId: HelpdeskSemanticId.optional() });
export type HelpdeskWebhookChoiceSelection = z.infer<typeof HelpdeskWebhookChoiceSelection>;

// Choice metadata is additive: `value` keeps the historical option-ID contract where a field semantic ID exists,
// while `displayValue` and `choiceSelections` provide automation-friendly labels and optional stable option IDs.
const HelpdeskWebhookChoiceDisplayValue = z.union([NonEmptyString, z.array(NonEmptyString).min(1)]);
type HelpdeskWebhookChoiceDisplayValue = z.infer<typeof HelpdeskWebhookChoiceDisplayValue>;
const isWebhookChoiceValue = (value: unknown): value is string | string[] => typeof value === "string" || (Array.isArray(value) && value.every((item) => typeof item === "string"));

const addWebhookChoiceMetadataErrors = (
  value: unknown,
  displayValue: HelpdeskWebhookChoiceDisplayValue | undefined,
  choiceSelections: readonly HelpdeskWebhookChoiceSelection[] | undefined,
  ctx: z.RefinementCtx,
): void => {
  if (displayValue === undefined && choiceSelections === undefined) return;
  if (displayValue === undefined || choiceSelections === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [displayValue === undefined ? "displayValue" : "choiceSelections"], message: "choice metadata must include displayValue and choiceSelections" });
    return;
  }
  if (!isWebhookChoiceValue(value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["choiceSelections"], message: "choice metadata requires a string answer value" });
    return;
  }
  if (Array.isArray(value) !== Array.isArray(displayValue)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["displayValue"], message: "choice displayValue must match answer cardinality" });
    return;
  }
  const displayValues = typeof displayValue === "string" ? [displayValue] : displayValue;
  const answerValues = typeof value === "string" ? [value] : value;
  if (answerValues.length !== displayValues.length || answerValues.length !== choiceSelections.length || displayValues.some((selection, index) => selection !== choiceSelections[index]?.value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["choiceSelections"], message: "choice selection labels must match displayValue in order" });
  }
};

export type HelpdeskWebhookValue = HelpdeskWebhookLeafValue | HelpdeskWebhookGroupValue;
export type HelpdeskWebhookGroupValue = {
  kind: "group";
  items: Array<{ answers: HelpdeskWebhookAnswer[] }>;
};
export type HelpdeskWebhookAnswer = {
  fieldId: string;
  label: string;
  semanticId?: HelpdeskSemanticId;
  semantic?: z.infer<typeof HelpdeskExternalResourceSemantic>;
  value: HelpdeskWebhookValue;
  displayValue?: HelpdeskWebhookChoiceDisplayValue;
  choiceSelections?: HelpdeskWebhookChoiceSelection[];
};

const HelpdeskWebhookValueSchema: z.ZodType<HelpdeskWebhookValue> = z.lazy(() => z.union([HelpdeskWebhookLeafValue, HelpdeskWebhookGroupValue]));
const helpdeskWebhookAnswerFields = {
  fieldId: z.string().uuid(),
  label: z.string(),
  semanticId: HelpdeskSemanticId.optional(),
  semantic: HelpdeskExternalResourceSemantic.optional(),
  value: HelpdeskWebhookValueSchema,
  displayValue: HelpdeskWebhookChoiceDisplayValue.optional(),
  choiceSelections: z.array(HelpdeskWebhookChoiceSelection).min(1).optional(),
};

const HelpdeskWebhookAnswerSchema = z.object(helpdeskWebhookAnswerFields).superRefine((answer, ctx) => addWebhookChoiceMetadataErrors(answer.value, answer.displayValue, answer.choiceSelections, ctx));

export const HelpdeskWebhookGroupValue = z.object({
  kind: z.literal("group"),
  items: z.array(
    z.object({
      answers: z.array(HelpdeskWebhookAnswerSchema),
    }),
  ),
});

export const HelpdeskWebhookValue = z.union([HelpdeskWebhookLeafValue, HelpdeskWebhookGroupValue]);

export const HelpdeskWebhookAnswer = HelpdeskWebhookAnswerSchema;

export const HelpdeskPeopleSearchResponse = z.object({ people: z.array(PersonRef) });
export type HelpdeskPeopleSearchResponse = z.infer<typeof HelpdeskPeopleSearchResponse>;

export const HelpdeskEntraGroupSearchResponse = z.object({ groups: z.array(EntraGroupRef) });
export type HelpdeskEntraGroupSearchResponse = z.infer<typeof HelpdeskEntraGroupSearchResponse>;

export const HelpdeskTeamRef = z.object({
  id: z.string().uuid(),
  linearTeamId: NonEmptyString,
  name: NonEmptyString,
});
export type HelpdeskTeamRef = z.infer<typeof HelpdeskTeamRef>;

export const HelpdeskIconUrl = z
  .string()
  .url()
  .max(2_000)
  .refine(
    (value) => {
      const url = new URL(value);
      return url.protocol === "https:" && url.username === "" && url.password === "";
    },
    { message: "Icon URL must be an HTTPS URL without credentials" },
  );

export const HELPDESK_DEPARTMENT_DESCRIPTION_MAX_LENGTH = 500;
export const HELPDESK_DEPARTMENT_GROUP_NAME_MAX_LENGTH = 200;
export const HELPDESK_OTHER_DEPARTMENT_GROUP_ID = "00000000-0000-4000-8000-000000000001";
const HelpdeskDepartmentDescription = z.preprocess((value) => (typeof value === "string" ? value.trim() || null : value), z.string().max(HELPDESK_DEPARTMENT_DESCRIPTION_MAX_LENGTH).nullable());

export const HelpdeskProjectRef = z.object({
  id: NonEmptyString,
  name: NonEmptyString,
});
export type HelpdeskProjectRef = z.infer<typeof HelpdeskProjectRef>;

export const HelpdeskDepartmentRef = z.object({
  description: HelpdeskDepartmentDescription.optional(),
  id: z.string().uuid(),
  iconUrl: HelpdeskIconUrl.nullable().optional(),
  name: NonEmptyString,
});
export type HelpdeskDepartmentRef = z.infer<typeof HelpdeskDepartmentRef>;

export const HelpdeskDepartmentSubcategory = z.object({
  id: z.string().uuid(),
  name: NonEmptyString.max(200),
  sortOrder: z.number().int().nonnegative(),
});
export type HelpdeskDepartmentSubcategory = z.infer<typeof HelpdeskDepartmentSubcategory>;

export const HelpdeskDepartmentSubcategories = z.object({
  department: HelpdeskDepartmentRef,
  subcategories: z.array(HelpdeskDepartmentSubcategory),
  unassignedSortOrder: z.number().int().nonnegative(),
});
export type HelpdeskDepartmentSubcategories = z.infer<typeof HelpdeskDepartmentSubcategories>;

export const HelpdeskDepartmentSubcategoriesResponse = HelpdeskDepartmentSubcategories;
export type HelpdeskDepartmentSubcategoriesResponse = z.infer<typeof HelpdeskDepartmentSubcategoriesResponse>;

export const HelpdeskDepartmentTeam = z.object({
  id: z.string().uuid(),
  key: NonEmptyString,
  linearTeamId: NonEmptyString,
  name: NonEmptyString,
});
export type HelpdeskDepartmentTeam = z.infer<typeof HelpdeskDepartmentTeam>;

export const HelpdeskDepartment = z.object({
  description: HelpdeskDepartmentDescription.optional(),
  id: z.string().uuid(),
  iconUrl: HelpdeskIconUrl.nullable().optional(),
  team: HelpdeskDepartmentTeam,
});
export type HelpdeskDepartment = z.infer<typeof HelpdeskDepartment>;

export const HELPDESK_INTERNAL_TENANT_ID = "00000000-0000-4000-8000-000000000001";
export const HELPDESK_TENANT_NAME_MAX_LENGTH = 200;
export const TENANT_SLUG_MAX_LENGTH = 40;
export const TENANT_SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;
export const TENANT_SLUG_REDIRECT_DAYS = 90;
export const INTERNAL_TENANT_DEFAULT_SLUG = "sf";
export const RESERVED_TENANT_SLUGS: readonly string[] = [
  "account",
  "admin",
  "api",
  "approvals",
  "assets",
  "auth",
  "connection-test",
  "external",
  "favicon",
  "gates",
  "healthz",
  "helpdesk",
  "index",
  "mcp",
  "me",
  "metrics",
  "profile",
  "readyz",
  "robots",
  "sw",
  "timeline",
  "webhooks",
];
export const TenantSlug = z
  .string()
  .regex(TENANT_SLUG_PATTERN)
  .refine((slug) => !RESERVED_TENANT_SLUGS.includes(slug), { message: "reserved_slug" });
export type TenantSlug = z.infer<typeof TenantSlug>;

export const HelpdeskTenant = z.object({
  id: z.string().uuid(),
  name: NonEmptyString.max(HELPDESK_TENANT_NAME_MAX_LENGTH),
  internal: z.boolean(),
  accessEnabled: z.boolean(),
  supportEmail: z.string().email().nullable().default(null),
  slug: z.string().regex(TENANT_SLUG_PATTERN),
  departments: z.array(HelpdeskDepartment),
});
export type HelpdeskTenant = z.infer<typeof HelpdeskTenant>;

// Admin-route view of a tenant: portalUrl is the absolute address on the origin that serves it (internal or public).
export const HelpdeskAdminTenant = HelpdeskTenant.extend({ portalUrl: z.string().url() });
export type HelpdeskAdminTenant = z.infer<typeof HelpdeskAdminTenant>;

// externalPortalOrigin is where tenants created from the admin UI (always external) will be served.
export const HelpdeskTenantsResponse = z.object({ tenants: z.array(HelpdeskAdminTenant), externalPortalOrigin: z.string().url() });
export type HelpdeskTenantsResponse = z.infer<typeof HelpdeskTenantsResponse>;
export const HelpdeskTenantResponse = z.object({ tenant: HelpdeskAdminTenant });
export type HelpdeskTenantResponse = z.infer<typeof HelpdeskTenantResponse>;

export const CreateHelpdeskTenantInput = z.object({ name: NonEmptyString.max(HELPDESK_TENANT_NAME_MAX_LENGTH), slug: TenantSlug }).strict();
export type CreateHelpdeskTenantInput = z.infer<typeof CreateHelpdeskTenantInput>;
export const SetHelpdeskTenantAccessInput = z.object({ enabled: z.boolean() }).strict();
export type SetHelpdeskTenantAccessInput = z.infer<typeof SetHelpdeskTenantAccessInput>;
export const SetHelpdeskTenantSupportEmailInput = z
  .object({
    supportEmail: z.preprocess((value) => (typeof value === "string" ? value.trim() || null : value), z.string().email().max(254).nullable()),
  })
  .strict();
export type SetHelpdeskTenantSupportEmailInput = z.infer<typeof SetHelpdeskTenantSupportEmailInput>;
export const HelpdeskSupportResponse = z.object({ supportEmail: z.string().email().nullable() }).strict();
export type HelpdeskSupportResponse = z.infer<typeof HelpdeskSupportResponse>;

export const RenameHelpdeskTenantInput = z.object({ name: NonEmptyString.max(HELPDESK_TENANT_NAME_MAX_LENGTH) }).strict();
export type RenameHelpdeskTenantInput = z.infer<typeof RenameHelpdeskTenantInput>;
export const UpdateHelpdeskTenantSlugInput = z.object({ slug: TenantSlug }).strict();
export type UpdateHelpdeskTenantSlugInput = z.infer<typeof UpdateHelpdeskTenantSlugInput>;

export const HelpdeskDepartmentsResponse = z.object({
  departments: z.array(HelpdeskDepartment),
});
export type HelpdeskDepartmentsResponse = z.infer<typeof HelpdeskDepartmentsResponse>;

export const HelpdeskDepartmentGroupSummary = z.object({
  id: z.string().uuid(),
  name: NonEmptyString.max(HELPDESK_DEPARTMENT_GROUP_NAME_MAX_LENGTH),
  protected: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
});
export type HelpdeskDepartmentGroupSummary = z.infer<typeof HelpdeskDepartmentGroupSummary>;

export const HelpdeskDepartmentGroup = HelpdeskDepartmentGroupSummary.extend({
  departments: z.array(HelpdeskDepartment.extend({ sortOrder: z.number().int().nonnegative() })),
});
export type HelpdeskDepartmentGroup = z.infer<typeof HelpdeskDepartmentGroup>;

export const HelpdeskDepartmentGroupsResponse = z.object({ groups: z.array(HelpdeskDepartmentGroup) });
export type HelpdeskDepartmentGroupsResponse = z.infer<typeof HelpdeskDepartmentGroupsResponse>;

export const HelpdeskDepartmentGroupDirectoryEntry = HelpdeskDepartmentGroupSummary.pick({ id: true, name: true }).extend({
  departmentIds: z.array(z.string().uuid()),
});
export type HelpdeskDepartmentGroupDirectoryEntry = z.infer<typeof HelpdeskDepartmentGroupDirectoryEntry>;

export const CreateHelpdeskDepartmentGroupInput = z.object({ name: NonEmptyString.max(HELPDESK_DEPARTMENT_GROUP_NAME_MAX_LENGTH) });
export type CreateHelpdeskDepartmentGroupInput = z.infer<typeof CreateHelpdeskDepartmentGroupInput>;

export const RenameHelpdeskDepartmentGroupInput = CreateHelpdeskDepartmentGroupInput;
export type RenameHelpdeskDepartmentGroupInput = z.infer<typeof RenameHelpdeskDepartmentGroupInput>;

export const ReorderHelpdeskDepartmentGroupsInput = z.object({ ids: z.array(z.string().uuid()) });
export type ReorderHelpdeskDepartmentGroupsInput = z.infer<typeof ReorderHelpdeskDepartmentGroupsInput>;

export const ReorderHelpdeskGroupDepartmentsInput = z.object({ ids: z.array(z.string().uuid()) });
export type ReorderHelpdeskGroupDepartmentsInput = z.infer<typeof ReorderHelpdeskGroupDepartmentsInput>;

export const MoveHelpdeskDepartmentInput = z.object({ groupId: z.string().uuid() });
export type MoveHelpdeskDepartmentInput = z.infer<typeof MoveHelpdeskDepartmentInput>;

export const HelpdeskLinearTeam = z.object({
  key: NonEmptyString,
  linearTeamId: NonEmptyString,
  linearTeamAncestorIds: z.array(NonEmptyString).optional(),
  documentSharingAvailable: z.boolean().optional(),
  name: NonEmptyString,
  nativeParentLinearTeamId: NonEmptyString.nullable(),
});
export type HelpdeskLinearTeam = z.infer<typeof HelpdeskLinearTeam>;

export const HelpdeskLinearTeamsResponse = z.object({
  teams: z.array(HelpdeskLinearTeam),
});
export type HelpdeskLinearTeamsResponse = z.infer<typeof HelpdeskLinearTeamsResponse>;

export const HelpdeskAdminLinearTeam = HelpdeskLinearTeam.extend({
  alreadyLinked: z.boolean(),
});
export type HelpdeskAdminLinearTeam = z.infer<typeof HelpdeskAdminLinearTeam>;

export const HelpdeskAdminLinearTeamsResponse = z.object({
  teams: z.array(HelpdeskAdminLinearTeam),
});
export type HelpdeskAdminLinearTeamsResponse = z.infer<typeof HelpdeskAdminLinearTeamsResponse>;

export const CreateHelpdeskDepartmentInput = z.object({
  linearTeamId: NonEmptyString,
});
export type CreateHelpdeskDepartmentInput = z.infer<typeof CreateHelpdeskDepartmentInput>;

export const HelpdeskDepartmentResponse = z.object({
  department: HelpdeskDepartment,
});
export type HelpdeskDepartmentResponse = z.infer<typeof HelpdeskDepartmentResponse>;

export const CreateHelpdeskSubcategoryInput = z.object({ name: NonEmptyString.max(200) });
export type CreateHelpdeskSubcategoryInput = z.infer<typeof CreateHelpdeskSubcategoryInput>;

export const RenameHelpdeskSubcategoryInput = CreateHelpdeskSubcategoryInput;
export type RenameHelpdeskSubcategoryInput = z.infer<typeof RenameHelpdeskSubcategoryInput>;

export const ReorderHelpdeskSubcategoriesInput = z.object({
  ids: z.array(z.string().uuid()),
  unassignedSortOrder: z.number().int().nonnegative(),
});
export type ReorderHelpdeskSubcategoriesInput = z.infer<typeof ReorderHelpdeskSubcategoriesInput>;

const HelpdeskPublishedFormVersionBase = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  version: z.number().int().positive(),
  name: NonEmptyString,
  description: z.string().trim().min(1).nullable(),
  iconUrl: HelpdeskIconUrl.nullable().optional(),
  linearSharing: z.object({ autoShareRequester: z.boolean(), requesterCanShareWithOthers: z.boolean() }).strict(),
  fields: z.array(HelpdeskFormField),
  publishedAt: Timestamp,
});

const refineHelpdeskVisibilityConditionTargets = (fields: readonly HelpdeskFormField[], ctx: z.RefinementCtx, path: (string | number)[] = ["fields"]): void => {
  const precedingIds = new Set<string>();

  for (const [index, field] of fields.entries()) {
    const condition = field.condition;
    if (condition !== undefined && condition !== null) {
      for (const conditionGroup of [...condition.matchAnyOf, ...condition.excludeAnyOf]) {
        for (const predicate of conditionGroup.predicates) {
          if (predicate.groupId !== undefined || !precedingIds.has(predicate.fieldId)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "condition"], message: "conditions may target only preceding top-level fields" });
          }
        }
      }
    }
    if (field.type === "group") refineHelpdeskVisibilityConditionTargets(field.fields, ctx, [...path, index, "fields"]);
    precedingIds.add(field.id);
  }
};

export const HelpdeskPublishedFormVersion = HelpdeskPublishedFormVersionBase.superRefine((form, ctx) => {
  const fieldsById = new Map<string, HelpdeskFormField>();
  const validateFieldScope = (fields: readonly HelpdeskFormField[], path: (string | number)[], nested: boolean): void => {
    const sortOrders = new Set<number>();
    let previousSortOrder: number | null = null;
    for (const [index, field] of fields.entries()) {
      if (fieldsById.has(field.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "id"], message: "field ids must be unique" });
      fieldsById.set(field.id, field);
      if (sortOrders.has(field.sortOrder)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "sortOrder"], message: "field sort orders must be unique" });
      sortOrders.add(field.sortOrder);
      if (previousSortOrder !== null && field.sortOrder < previousSortOrder)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path, index, "sortOrder"],
          message: nested ? "group fields must be ordered by ascending sortOrder" : "fields must be ordered by ascending sortOrder",
        });
      previousSortOrder = field.sortOrder;
      if (field.type === "choice" && new Set(field.options.map((option) => option.label.trim().toLocaleLowerCase())).size !== field.options.length)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, index, "options"], message: "choice options must be unique" });
      if (field.type === "group") validateFieldScope(field.fields, [...path, index, "fields"], true);
    }
  };

  validateFieldScope(form.fields, ["fields"], false);

  for (const error of helpdeskSemanticIdErrors(form.fields)) {
    const message =
      error.code === "duplicate"
        ? `semantic ID '${error.semanticId}' must be unique`
        : error.code === "invalid"
          ? `semantic ID '${error.semanticId}' is invalid`
          : `semantic ID '${error.semanticId}' requires a group semantic ID`;
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields"], message });
  }

  refineHelpdeskVisibilityConditionTargets(form.fields, ctx);
});
export type HelpdeskPublishedFormVersion = z.infer<typeof HelpdeskPublishedFormVersion>;

export const HelpdeskPublishedFormSummary = HelpdeskPublishedFormVersionBase.pick({
  id: true,
  formId: true,
  name: true,
  description: true,
  iconUrl: true,
}).extend({ hidden: z.boolean() });
export type HelpdeskPublishedFormSummary = z.infer<typeof HelpdeskPublishedFormSummary>;

export const HelpdeskCatalogSection = z.object({
  subcategory: HelpdeskDepartmentSubcategory.nullable(),
  forms: z.array(HelpdeskPublishedFormSummary),
});
export type HelpdeskCatalogSection = z.infer<typeof HelpdeskCatalogSection>;

export const HelpdeskDepartmentForms = z.object({
  department: HelpdeskDepartmentRef,
  sections: z.array(HelpdeskCatalogSection),
});
export type HelpdeskDepartmentForms = z.infer<typeof HelpdeskDepartmentForms>;

export const HelpdeskCatalogGroup = z.object({ categories: z.array(HelpdeskDepartmentRef), id: z.string().uuid(), name: NonEmptyString });
export type HelpdeskCatalogGroup = z.infer<typeof HelpdeskCatalogGroup>;

export const HelpdeskCatalogResponse = z.object({ categories: z.array(HelpdeskDepartmentRef), groups: z.array(HelpdeskCatalogGroup).optional() });
export type HelpdeskCatalogResponse = z.infer<typeof HelpdeskCatalogResponse>;

export const HelpdeskCatalogForm = z.intersection(
  HelpdeskPublishedFormVersion,
  z.object({
    hidden: z.boolean(),
    latestVersionId: z.string().uuid().nullable(),
    managedFieldVersionToken: NonEmptyString,
    superseded: z.boolean(),
  }),
);
export type HelpdeskCatalogForm = z.infer<typeof HelpdeskCatalogForm>;

export const HelpdeskExternalCatalogFormResponse = z.object({
  form: HelpdeskCatalogForm,
});
export type HelpdeskExternalCatalogFormResponse = z.infer<typeof HelpdeskExternalCatalogFormResponse>;

// `managedFieldVersionToken` is repeated at the top level for connected apps; `form` keeps its own copy for the web UI.
// `requestSchema` is the JSON Schema of this version's submission body (see the bearer OpenAPI document).
export const HelpdeskCatalogFormResponse = HelpdeskExternalCatalogFormResponse.extend({
  managedFieldVersionToken: NonEmptyString,
  requestSchema: z.record(z.unknown()),
});
export type HelpdeskCatalogFormResponse = z.infer<typeof HelpdeskCatalogFormResponse>;

// `versionId` is the current published version; by-form's `latestVersionId` instead means "a newer version than the one requested, or null".
export const HelpdeskCatalogFormListItem = z.object({
  formId: z.string().uuid(),
  versionId: z.string().uuid(),
  name: NonEmptyString,
  description: z.string().nullable(),
  departments: z.array(HelpdeskDepartmentRef.pick({ id: true, name: true })),
});
export type HelpdeskCatalogFormListItem = z.infer<typeof HelpdeskCatalogFormListItem>;

export const HelpdeskCatalogFormListResponse = z.object({ forms: z.array(HelpdeskCatalogFormListItem) });
export type HelpdeskCatalogFormListResponse = z.infer<typeof HelpdeskCatalogFormListResponse>;

export const HelpdeskActor = z.object({
  entraId: NonEmptyString,
  email: z.string().email(),
  name: NonEmptyString,
});
export type HelpdeskActor = z.infer<typeof HelpdeskActor>;

export const HELPDESK_CONNECTED_APP_NAME_MAX_LENGTH = 100;

export const HelpdeskConnectedAppCapability = z.enum(["read_forms", "submit_requests"]);
export type HelpdeskConnectedAppCapability = z.infer<typeof HelpdeskConnectedAppCapability>;
export const HelpdeskConnectedAppFormAccessMode = z.enum(["all", "selected"]);
export type HelpdeskConnectedAppFormAccessMode = z.infer<typeof HelpdeskConnectedAppFormAccessMode>;

export const HelpdeskConnectedAppAccess = z.object({
  capability: HelpdeskConnectedAppCapability,
  formAccessMode: HelpdeskConnectedAppFormAccessMode,
  formIds: z.array(z.string().uuid()),
  revision: z.number().int().nonnegative(),
});
export type HelpdeskConnectedAppAccess = z.infer<typeof HelpdeskConnectedAppAccess>;
export const HelpdeskConnectedAppAccessResponse = z.object({ access: HelpdeskConnectedAppAccess });
export type HelpdeskConnectedAppAccessResponse = z.infer<typeof HelpdeskConnectedAppAccessResponse>;
export const SaveHelpdeskConnectedAppAccessInput = HelpdeskConnectedAppAccess.omit({ revision: true }).extend({ expectedRevision: z.number().int().nonnegative() }).strict();
export type SaveHelpdeskConnectedAppAccessInput = z.infer<typeof SaveHelpdeskConnectedAppAccessInput>;
export const HelpdeskConnectedAppFormChoice = z.object({
  id: z.string().uuid(),
  name: z.string(),
  state: z.enum(["draft", "published"]),
  hidden: z.boolean(),
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type HelpdeskConnectedAppFormChoice = z.infer<typeof HelpdeskConnectedAppFormChoice>;
export const HelpdeskConnectedAppFormChoicesResponse = z.object({ forms: z.array(HelpdeskConnectedAppFormChoice) });
export type HelpdeskConnectedAppFormChoicesResponse = z.infer<typeof HelpdeskConnectedAppFormChoicesResponse>;

const HelpdeskConnectedAppBase = z.object({
  id: z.string().uuid(),
  name: NonEmptyString.max(HELPDESK_CONNECTED_APP_NAME_MAX_LENGTH),
  enabled: z.boolean(),
  capability: HelpdeskConnectedAppCapability,
  formAccessMode: HelpdeskConnectedAppFormAccessMode,
  accessRevision: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export const HELPDESK_SERVICE_APP_KEY_LIMIT = 2;
export const HELPDESK_SERVICE_APP_KEY_DEFAULT_EXPIRY_DAYS = 365;
export const HelpdeskServiceAppKeyExpiryDays = z.union([z.literal(90), z.literal(180), z.literal(365), z.null()]);
export type HelpdeskServiceAppKeyExpiryDays = z.infer<typeof HelpdeskServiceAppKeyExpiryDays>;
export const HelpdeskServiceAppKeyStatus = z.enum(["active", "expired", "revoked"]);
export type HelpdeskServiceAppKeyStatus = z.infer<typeof HelpdeskServiceAppKeyStatus>;
// Never carries the token or its hash; `hint` is `lpsa_…` plus the last four characters.
export const HelpdeskServiceAppKey = z.object({
  id: z.string().uuid(),
  hint: z.string(),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  status: HelpdeskServiceAppKeyStatus,
});
export type HelpdeskServiceAppKey = z.infer<typeof HelpdeskServiceAppKey>;
export const HelpdeskServiceAppKeysResponse = z.object({ keys: z.array(HelpdeskServiceAppKey) });
export type HelpdeskServiceAppKeysResponse = z.infer<typeof HelpdeskServiceAppKeysResponse>;
export const CreateHelpdeskServiceAppKeyInput = z.object({ expiresInDays: HelpdeskServiceAppKeyExpiryDays.default(HELPDESK_SERVICE_APP_KEY_DEFAULT_EXPIRY_DAYS) }).strict();
export type CreateHelpdeskServiceAppKeyInput = z.infer<typeof CreateHelpdeskServiceAppKeyInput>;
// The token is shown exactly once, in this response.
export const CreateHelpdeskServiceAppKeyResponse = z.object({ key: HelpdeskServiceAppKey, token: z.string() });
export type CreateHelpdeskServiceAppKeyResponse = z.infer<typeof CreateHelpdeskServiceAppKeyResponse>;

export const HELPDESK_SERVICE_APP_CONTACT_EMAIL_MAX_LENGTH = 254;
export const HelpdeskServiceAppContactEmail = z.string().trim().toLowerCase().email().max(HELPDESK_SERVICE_APP_CONTACT_EMAIL_MAX_LENGTH);

// Further kinds join this union; appId stays top-level for the Entra delegated kind.
export const HelpdeskConnectedApp = z.discriminatedUnion("kind", [
  HelpdeskConnectedAppBase.extend({ kind: z.literal("entra_delegated"), appId: z.string().uuid() }),
  HelpdeskConnectedAppBase.extend({ kind: z.literal("service"), contactEmail: z.string().email() }),
]);
export type HelpdeskConnectedApp = z.infer<typeof HelpdeskConnectedApp>;

const HelpdeskConnectedAppForms = z.array(z.object({ id: z.string().uuid(), name: z.string() }));
export const HelpdeskConnectedAppListItem = z.discriminatedUnion("kind", [
  HelpdeskConnectedAppBase.extend({ kind: z.literal("entra_delegated"), appId: z.string().uuid(), forms: HelpdeskConnectedAppForms }),
  HelpdeskConnectedAppBase.extend({ kind: z.literal("service"), contactEmail: z.string().email(), forms: HelpdeskConnectedAppForms, keys: z.array(HelpdeskServiceAppKey) }),
]);
export type HelpdeskConnectedAppListItem = z.infer<typeof HelpdeskConnectedAppListItem>;

export const HelpdeskConnectedAppsResponse = z.object({ apps: z.array(HelpdeskConnectedAppListItem) });
export type HelpdeskConnectedAppsResponse = z.infer<typeof HelpdeskConnectedAppsResponse>;
export const HelpdeskConnectedAppResponse = z.object({ app: HelpdeskConnectedApp });
export type HelpdeskConnectedAppResponse = z.infer<typeof HelpdeskConnectedAppResponse>;

const ConnectedAppName = NonEmptyString.max(HELPDESK_CONNECTED_APP_NAME_MAX_LENGTH);
// `kind` defaults to the Entra delegated kind so pre-existing clients that omit it keep working.
export const CreateHelpdeskConnectedAppInput = z.preprocess(
  (input) => (typeof input === "object" && input !== null && !("kind" in input) ? { ...input, kind: "entra_delegated" } : input),
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("entra_delegated"), appId: z.string().uuid(), name: ConnectedAppName }).strict(),
    z.object({ kind: z.literal("service"), name: ConnectedAppName, contactEmail: HelpdeskServiceAppContactEmail }).strict(),
  ]),
);
export type CreateHelpdeskConnectedAppInput = z.infer<typeof CreateHelpdeskConnectedAppInput>;
export const UpdateHelpdeskConnectedAppInput = z
  .object({ name: NonEmptyString.max(HELPDESK_CONNECTED_APP_NAME_MAX_LENGTH).optional(), enabled: z.boolean().optional() })
  .strict()
  .refine((input) => input.name !== undefined || input.enabled !== undefined);
export type UpdateHelpdeskConnectedAppInput = z.infer<typeof UpdateHelpdeskConnectedAppInput>;

export const HelpdeskRequesterActor = z.union([
  HelpdeskActor,
  HelpdeskActor.extend({ entraId: z.null(), portalUserId: z.string().trim().min(1).max(256) }),
  HelpdeskActor.extend({ entraId: z.null(), connectedAppId: z.string().uuid() }),
]);
export type HelpdeskRequesterActor = z.infer<typeof HelpdeskRequesterActor>;

export const HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAMES_MAX = 1_000;
export const HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAME_MAX_LENGTH = 500;

const comparePrincipalGroupNames = (left: string, right: string): number => (left === right ? 0 : left < right ? -1 : 1);
const sortPrincipalGroupNames = (names: readonly string[]): string[] => [...names].sort(comparePrincipalGroupNames);
const samePrincipalGroupNames = (left: readonly string[], right: readonly string[]): boolean => left.length === right.length && left.every((name, index) => name === right[index]);
const principalGroupConfigurationRevisionCheck = (
  configuration: { names: readonly string[]; revision: number; recipientRules?: HelpdeskCloudInfrastructureRecipientRules },
  context: z.RefinementCtx,
): void => {
  if (configuration.revision === 0 && (configuration.names.length !== 0 || (configuration.recipientRules?.include.length ?? 0) > 0 || (configuration.recipientRules?.exclude.length ?? 0) > 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["names"], message: "revision 0 requires an empty principal-group configuration" });
  }
};

export const HelpdeskCloudInfrastructurePrincipalGroupNames = z
  .array(z.string().trim().min(1).max(HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAME_MAX_LENGTH))
  .max(HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAMES_MAX)
  .superRefine((names, context) => {
    const seen = new Set<string>();
    for (const [index, name] of names.entries()) {
      if (seen.has(name)) context.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: "principal group names must be unique" });
      seen.add(name);
    }
  })
  .transform(sortPrincipalGroupNames);
export type HelpdeskCloudInfrastructurePrincipalGroupNames = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupNames>;

export const HelpdeskCloudInfrastructurePrincipalGroupNamesStored = z
  .array(z.string().min(1).max(HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAME_MAX_LENGTH))
  .max(HELPDESK_CLOUD_INFRASTRUCTURE_PRINCIPAL_GROUP_NAMES_MAX)
  .superRefine((names, context) => {
    const seen = new Set<string>();
    for (const [index, name] of names.entries()) {
      if (name !== name.trim()) context.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: "stored principal group names must be trimmed" });
      if (seen.has(name)) context.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: "stored principal group names must be unique" });
      seen.add(name);
    }
    if (!samePrincipalGroupNames(names, sortPrincipalGroupNames(names))) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [], message: "stored principal group names must be canonically sorted" });
    }
  });
export type HelpdeskCloudInfrastructurePrincipalGroupNamesStored = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupNamesStored>;

const cloudInfrastructureConfigurationFields = {
  recipientRules: HelpdeskCloudInfrastructureRecipientRules.optional(),
  roleTemplates: HelpdeskCloudInfrastructureRoleTemplates.optional(),
};

function completeCloudInfrastructureConfiguration<T extends { names: string[]; recipientRules?: HelpdeskCloudInfrastructureRecipientRules; roleTemplates?: HelpdeskCloudInfrastructureRoleTemplates }>(
  configuration: T,
): T & { recipientRules: HelpdeskCloudInfrastructureRecipientRules; roleTemplates: HelpdeskCloudInfrastructureRoleTemplates } {
  return {
    ...configuration,
    recipientRules: configuration.recipientRules ?? migrateHelpdeskCloudInfrastructureRecipientRules(configuration.names),
    roleTemplates: configuration.roleTemplates ?? defaultHelpdeskCloudInfrastructureRoleTemplates(),
  };
}

export const HelpdeskCloudInfrastructurePrincipalGroupConfiguration = z
  .object({ names: HelpdeskCloudInfrastructurePrincipalGroupNames, revision: z.number().int().nonnegative(), ...cloudInfrastructureConfigurationFields })
  .strict()
  .superRefine(principalGroupConfigurationRevisionCheck)
  .transform(completeCloudInfrastructureConfiguration);
export type HelpdeskCloudInfrastructurePrincipalGroupConfiguration = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupConfiguration>;

export const HelpdeskCloudInfrastructurePrincipalGroupConfigurationStored = z
  .object({ names: HelpdeskCloudInfrastructurePrincipalGroupNamesStored, revision: z.number().int().nonnegative(), ...cloudInfrastructureConfigurationFields })
  .strict()
  .superRefine(principalGroupConfigurationRevisionCheck)
  .transform(completeCloudInfrastructureConfiguration);
export type HelpdeskCloudInfrastructurePrincipalGroupConfigurationStored = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupConfigurationStored>;

export const SaveHelpdeskCloudInfrastructurePrincipalGroupConfigurationInput = z
  .object({ expectedRevision: z.number().int().nonnegative(), names: HelpdeskCloudInfrastructurePrincipalGroupNames.optional(), ...cloudInfrastructureConfigurationFields })
  .strict()
  .superRefine((value, context) => {
    if (value.names === undefined && value.recipientRules === undefined)
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["recipientRules"], message: "Provide recipient rules or legacy group names" });
    if (value.recipientRules !== undefined && value.roleTemplates === undefined)
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["roleTemplates"], message: "Provide role templates with recipient rules" });
    if (value.roleTemplates !== undefined && value.recipientRules === undefined)
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["recipientRules"], message: "Provide recipient rules with role templates" });
  })
  .transform((value) =>
    completeCloudInfrastructureConfiguration({
      ...value,
      names:
        value.recipientRules === undefined
          ? (value.names ?? [])
          : sortPrincipalGroupNames([...new Set(value.recipientRules.include.filter((rule) => rule.match === "exact").map((rule) => rule.pattern))]),
    }),
  );
export type SaveHelpdeskCloudInfrastructurePrincipalGroupConfigurationInput = z.infer<typeof SaveHelpdeskCloudInfrastructurePrincipalGroupConfigurationInput>;

const HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponseValue = z
  .object({
    names: HelpdeskCloudInfrastructurePrincipalGroupNamesStored,
    ...cloudInfrastructureConfigurationFields,
    revision: z.number().int().nonnegative(),
    updatedAt: Timestamp.nullable(),
    updatedBy: HelpdeskActor.nullable(),
  })
  .strict()
  .superRefine((configuration, context) => {
    principalGroupConfigurationRevisionCheck(configuration, context);
    if (configuration.revision === 0) {
      if (configuration.updatedAt !== null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["updatedAt"], message: "revision 0 cannot have an updatedAt value" });
      if (configuration.updatedBy !== null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["updatedBy"], message: "revision 0 cannot have an updatedBy value" });
      return;
    }
    if (configuration.updatedAt === null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["updatedAt"], message: "positive revisions require updatedAt" });
    if (configuration.updatedBy === null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["updatedBy"], message: "positive revisions require updatedBy" });
  })
  .transform(completeCloudInfrastructureConfiguration);

export const HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponse = z
  .object({
    capabilities: z.object({ manage: z.boolean() }).strict().default({ manage: false }),
    configuration: HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponseValue,
  })
  .strict();
export type HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponse = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponse>;

export const HelpdeskCloudInfrastructurePrincipalGroupConfigurationConflictResponse = z
  .object({
    error: z.literal("configuration_conflict"),
    configuration: HelpdeskCloudInfrastructurePrincipalGroupConfigurationResponseValue,
  })
  .strict();
export type HelpdeskCloudInfrastructurePrincipalGroupConfigurationConflictResponse = z.infer<typeof HelpdeskCloudInfrastructurePrincipalGroupConfigurationConflictResponse>;

export const HelpdeskPreviewAcknowledgement = z.object({
  acknowledgedAt: Timestamp,
  actor: HelpdeskActor,
  revision: z.number().int().nonnegative(),
});
export type HelpdeskPreviewAcknowledgement = z.infer<typeof HelpdeskPreviewAcknowledgement>;

export const HelpdeskFormVersionState = z.enum(["draft", "published", "superseded"]);
export type HelpdeskFormVersionState = z.infer<typeof HelpdeskFormVersionState>;

export const HelpdeskManagedFieldScope = z.enum(["company", "department"]);
export type HelpdeskManagedFieldScope = z.infer<typeof HelpdeskManagedFieldScope>;

export const HelpdeskManagedFieldVersion = z.object({
  id: z.string().uuid(),
  managedFieldId: z.string().uuid(),
  state: HelpdeskFormVersionState,
  version: z.number().int().positive().nullable(),
  revision: z.number().int().nonnegative(),
  definition: HelpdeskManagedFieldDefinition,
  createdBy: HelpdeskActor,
  updatedBy: HelpdeskActor,
  restoredFromVersion: z.number().int().positive().nullable(),
  publishedAt: Timestamp.nullable(),
  publishedBy: HelpdeskActor.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type HelpdeskManagedFieldVersion = z.infer<typeof HelpdeskManagedFieldVersion>;

export const HelpdeskManagedFieldSummary = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  libraryName: NonEmptyString,
  scope: HelpdeskManagedFieldScope,
  department: HelpdeskDepartmentRef.nullable(),
  type: HelpdeskManagedFieldType,
  archivedAt: Timestamp.nullable(),
  publishedVersion: z.number().int().positive().nullable(),
  updatedAt: Timestamp,
});
export type HelpdeskManagedFieldSummary = z.infer<typeof HelpdeskManagedFieldSummary>;

export const HelpdeskManagedField = HelpdeskManagedFieldSummary.extend({
  draft: HelpdeskManagedFieldVersion.nullable(),
  published: HelpdeskManagedFieldVersion.nullable(),
  history: z.array(HelpdeskManagedFieldVersion),
});
export type HelpdeskManagedField = z.infer<typeof HelpdeskManagedField>;

export const HelpdeskManagedFieldsResponse = z.object({ departmentGroups: z.array(HelpdeskDepartmentGroupDirectoryEntry), fields: z.array(HelpdeskManagedFieldSummary) });
export type HelpdeskManagedFieldsResponse = z.infer<typeof HelpdeskManagedFieldsResponse>;
export const HelpdeskManagedFieldResponse = z.object({ field: HelpdeskManagedField });
export type HelpdeskManagedFieldResponse = z.infer<typeof HelpdeskManagedFieldResponse>;
export const HelpdeskVersionToken = z.object({ versionId: z.string().uuid(), revision: z.number().int().nonnegative() });
export type HelpdeskVersionToken = z.infer<typeof HelpdeskVersionToken>;
export const HelpdeskManagedFieldMutationResponse = HelpdeskManagedFieldResponse.extend({ appliedVersion: HelpdeskVersionToken.nullable() });
export type HelpdeskManagedFieldMutationResponse = z.infer<typeof HelpdeskManagedFieldMutationResponse>;

const CreateHelpdeskManagedFieldInputBase = {
  tenantId: z.string().uuid().optional(),
  libraryName: NonEmptyString.max(200),
  departmentId: z.string().uuid().nullable(),
};
const HelpdeskManagedExternalResourceComponentIdentity = HelpdeskExternalResourceSourceIdentity;
export const CreateHelpdeskManagedFieldInput = z
  .object({
    ...CreateHelpdeskManagedFieldInputBase,
    type: HelpdeskManagedFieldType,
    component: HelpdeskManagedExternalResourceComponentIdentity.optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    const hasComponentProperty = "component" in input;
    const hasComponent = input.component !== undefined;
    if (input.type === "externalResource") {
      if (!hasComponent) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["component"], message: "external resources require a component identity" });
    } else if (hasComponentProperty) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["component"], message: "component identity is only valid for external resources" });
    }
  });
export type CreateHelpdeskManagedFieldInput = z.infer<typeof CreateHelpdeskManagedFieldInput>;

export const SaveHelpdeskManagedFieldDraftInput = z.object({
  expectedVersionId: z.string().uuid(),
  libraryName: NonEmptyString.max(200),
  revision: z.number().int().nonnegative(),
  definition: HelpdeskManagedFieldDefinition,
});
export type SaveHelpdeskManagedFieldDraftInput = z.infer<typeof SaveHelpdeskManagedFieldDraftInput>;

export const RestoreHelpdeskManagedFieldInput = z.object({ expectedVersionId: z.string().uuid(), versionId: z.string().uuid(), revision: z.number().int().nonnegative() });
export type RestoreHelpdeskManagedFieldInput = z.infer<typeof RestoreHelpdeskManagedFieldInput>;

export const HelpdeskManagedFieldPublicationBlocker = z.object({ formId: z.string().uuid(), formName: NonEmptyString, destinationId: z.string().uuid(), departmentName: NonEmptyString });
export type HelpdeskManagedFieldPublicationBlocker = z.infer<typeof HelpdeskManagedFieldPublicationBlocker>;

export const HelpdeskManagedFieldResolution = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  libraryName: NonEmptyString,
  scope: HelpdeskManagedFieldScope,
  department: HelpdeskDepartmentRef.nullable(),
  archivedAt: Timestamp.nullable(),
  versionId: z.string().uuid(),
  version: z.number().int().positive(),
  definition: HelpdeskManagedFieldDefinition,
});
export type HelpdeskManagedFieldResolution = z.infer<typeof HelpdeskManagedFieldResolution>;

export const HelpdeskIssueFieldRule = z
  .object({
    condition: HelpdeskDestinationCondition.nullable(),
    labelIds: z.array(NonEmptyString),
    labelGroupIdsByLabelId: z.record(NonEmptyString.nullable()).optional(),
    labelSourceFieldId: z.string().uuid().nullable().optional(),
    releaseIds: z.array(NonEmptyString).optional(),
    assigneeId: NonEmptyString.nullable(),
    dueDateFieldId: NonEmptyString.nullable().optional(),
    name: NonEmptyString.max(100).optional(),
    priority: HelpdeskLinearPriority.nullable(),
    prioritySourceFieldId: z.string().uuid().nullable().optional(),
    workflowStateId: NonEmptyString.nullable(),
    workflowStateSourceFieldId: z.string().uuid().nullable().optional(),
  })
  .superRefine((rule, ctx) => {
    if (rule.labelGroupIdsByLabelId !== undefined) {
      const labelIds = new Set(rule.labelIds);
      const metadataLabelIds = Object.keys(rule.labelGroupIdsByLabelId);
      if (metadataLabelIds.length !== labelIds.size || metadataLabelIds.some((labelId) => !labelIds.has(labelId))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Label-group metadata must correspond exactly to the static labels", path: ["labelGroupIdsByLabelId"] });
      }
    }
    if (rule.labelSourceFieldId != null && rule.labelIds.length > 0)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A label rule cannot configure both static labels and a source field", path: ["labelSourceFieldId"] });
    if (rule.prioritySourceFieldId != null && rule.priority !== null)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A priority rule cannot configure both a static priority and a source field", path: ["prioritySourceFieldId"] });
    if (rule.workflowStateSourceFieldId != null && rule.workflowStateId !== null)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "A workflow-state rule cannot configure both a static state and a source field", path: ["workflowStateSourceFieldId"] });
  })
  .refine(
    (rule) =>
      rule.labelIds.length > 0 ||
      rule.labelSourceFieldId != null ||
      (rule.releaseIds?.length ?? 0) > 0 ||
      rule.assigneeId !== null ||
      (rule.dueDateFieldId ?? null) !== null ||
      rule.priority !== null ||
      rule.prioritySourceFieldId != null ||
      rule.workflowStateId !== null ||
      rule.workflowStateSourceFieldId != null,
    {
      message: "An issue field rule must configure at least one issue field",
    },
  )
  .refine((rule) => new Set(rule.labelIds).size === rule.labelIds.length, {
    message: "An issue field rule cannot repeat a label",
  })
  .refine((rule) => new Set(rule.releaseIds ?? []).size === (rule.releaseIds?.length ?? 0), {
    message: "An issue field rule cannot repeat a release",
  });
export type HelpdeskIssueFieldRule = z.infer<typeof HelpdeskIssueFieldRule>;

export const HelpdeskLinearIssueFieldCatalog = z.object({
  approvalLifecycleMode: z.enum(["labels", "statuses"]).optional(),
  labels: z.array(
    z.object({
      id: NonEmptyString,
      name: NonEmptyString,
      color: NonEmptyString,
      labelGroupId: NonEmptyString.nullable(),
      labelGroupName: NonEmptyString.nullable(),
      linearTeamId: NonEmptyString.nullable(),
    }),
  ),
  releases: z.array(z.object({ id: NonEmptyString, name: NonEmptyString, stage: NonEmptyString })),
  assignees: z.array(z.object({ id: NonEmptyString, name: NonEmptyString, email: z.string().email() })),
  workflowStates: z.array(z.object({ id: NonEmptyString, name: NonEmptyString, type: NonEmptyString })),
});
export type HelpdeskLinearIssueFieldCatalog = z.infer<typeof HelpdeskLinearIssueFieldCatalog>;

export const HelpdeskLinearTemplateSummary = z.object({ id: NonEmptyString, name: NonEmptyString, description: z.string().nullable() });
export type HelpdeskLinearTemplateSummary = z.infer<typeof HelpdeskLinearTemplateSummary>;

export const HelpdeskLinearTemplateImportPreview = z.object({
  template: HelpdeskLinearTemplateSummary,
  titleTemplate: NonEmptyString.max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH),
  bodyTemplate: NonEmptyString.max(HELPDESK_BODY_TEMPLATE_MAX_LENGTH),
  fields: z.array(HelpdeskFormFieldInput),
  defaults: z.object({
    linearProjectId: NonEmptyString.nullable(),
    priority: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).nullable(),
    workflowStateId: NonEmptyString.nullable(),
  }),
  warnings: z.array(NonEmptyString),
});
export type HelpdeskLinearTemplateImportPreview = z.infer<typeof HelpdeskLinearTemplateImportPreview>;

// Every value here is raised by publication validation: a template placeholder that no longer resolves, a Linear
// team the app can no longer read, a project that either no longer exists or no longer includes its pair's team,
// a condition predicate that references a field or value that no longer exists, or a webhook destination whose URL
// is missing or malformed.
export const HelpdeskDestinationValidationError = z.enum([
  "approval_gate_unavailable",
  "approval_interop_invalid",
  "team_unavailable",
  "project_unavailable",
  "project_team_mismatch",
  "issue_field_unavailable",
  "template_invalid",
  "condition_invalid",
  "webhook_url_invalid",
  "webhook_status_invalid",
]);
export type HelpdeskDestinationValidationError = z.infer<typeof HelpdeskDestinationValidationError>;

// A destination routes a submission somewhere. "linear" creates a Linear issue from title/body templates; "webhook"
// POSTs the submission as structured JSON to an n8n (or other) endpoint. Both are gated by the shared condition.
export const HelpdeskDestinationKind = z.enum(["linear", "webhook"]);
export type HelpdeskDestinationKind = z.infer<typeof HelpdeskDestinationKind>;

export const HelpdeskDraftDepartment = z.object({
  documentSharingAvailable: z.boolean().optional(),
  department: HelpdeskDepartmentRef,
  category: z.boolean(),
  subcategory: HelpdeskDepartmentSubcategory.nullable().optional(),
  subcategories: z.array(HelpdeskDepartmentSubcategory).optional(),
  readyAt: Timestamp.nullable(),
  readyBy: HelpdeskActor.nullable(),
  callerIsMember: z.boolean(),
  callerCanManageContribution: z.boolean(),
  callerCanEditContribution: z.boolean(),
  // A Department contributes zero or more destination/template pairs, so having none is not a validation failure;
  // every failure a Department can carry is raised against one of its pairs.
  validationError: HelpdeskDestinationValidationError.nullable(),
});
export type HelpdeskDraftDepartment = z.infer<typeof HelpdeskDraftDepartment>;

// Shared supertype fields (mirror the class-table-inheritance core row). Every destination kind carries identity,
// the routing condition, and the validation verdict; the kind-specific fields live on each variant below.
const helpdeskDestinationBase = {
  id: z.string().uuid(),
  departmentId: z.string().uuid(),
  condition: HelpdeskDestinationCondition.nullable(),
  validationError: HelpdeskDestinationValidationError.nullable(),
};

export const HelpdeskLinearDestination = z.object({
  approvalInterop: HelpdeskApprovalInteropConfig.optional(),
  ...helpdeskDestinationBase,
  attachSubmittedFiles: z.boolean(),
  autoShareRequester: z.boolean(),
  documentSharingEnabled: z.boolean().optional(),
  coordinatorShareAndAssign: z.boolean().optional(),
  autoSubscribeRequester: z.boolean().optional(),
  statusEmailMode: HelpdeskStatusEmailMode.optional(),
  kind: z.literal("linear"),
  team: HelpdeskTeamRef,
  linearTeamAncestorIds: z.array(NonEmptyString).optional(),
  linearProjectId: NonEmptyString.nullable(),
  communicationMode: HelpdeskCommunicationMode,
  titleTemplate: NonEmptyString,
  bodyTemplate: NonEmptyString,
  issueFieldRules: z.array(HelpdeskIssueFieldRule).default([]),
  requesterCanShareWithOthers: z.boolean(),
});
export type HelpdeskLinearDestination = z.infer<typeof HelpdeskLinearDestination>;

export const HelpdeskWebhookDependencyMilestone = z.enum(["created", "approved", "finalized", "status"]);
export type HelpdeskWebhookDependencyMilestone = z.infer<typeof HelpdeskWebhookDependencyMilestone>;

export const isHelpdeskApprovalMilestone = (milestone: HelpdeskWebhookDependencyMilestone): boolean => milestone === "approved" || milestone === "finalized";
export const isHelpdeskStatusMilestone = (milestone: HelpdeskWebhookDependencyMilestone): boolean => milestone === "status";

const helpdeskStatusDependencyFields = {
  stateIds: z.array(z.string().min(1)).optional(),
  anyTerminal: z.boolean().optional(),
};

export const HelpdeskLinearDependency = z.object({
  linearDestinationId: z.string().uuid(),
  field: z
    .string()
    .max(100)
    .regex(/^[A-Za-z][A-Za-z0-9_]*$/, "Field must start with a letter and contain only letters, numbers, and underscores"),
  milestone: HelpdeskWebhookDependencyMilestone.default("created"),
  ...helpdeskStatusDependencyFields,
});
export type HelpdeskLinearDependency = z.infer<typeof HelpdeskLinearDependency>;

const refineUniqueHelpdeskDependencies = <Dependency extends { field: string }>(
  dependencies: readonly Dependency[],
  destinationId: (dependency: Dependency) => string,
  destinationPath: string,
  ctx: z.RefinementCtx,
): void => {
  const destinationIds = new Set<string>();
  const fields = new Set<string>();
  for (const [index, dependency] of dependencies.entries()) {
    const dependencyDestinationId = destinationId(dependency);
    if (destinationIds.has(dependencyDestinationId)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Linear destinations must be unique", path: [index, destinationPath] });
    if (fields.has(dependency.field)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Fields must be unique", path: [index, "field"] });
    destinationIds.add(dependencyDestinationId);
    fields.add(dependency.field);
  }
};

const HelpdeskLinearDependencies = z
  .array(HelpdeskLinearDependency)
  .superRefine((dependencies, ctx) => refineUniqueHelpdeskDependencies(dependencies, (dependency) => dependency.linearDestinationId, "linearDestinationId", ctx))
  .default([]);

export const HelpdeskWebhookDependencyMode = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("selected") }),
  z.object({ mode: z.literal("all_routed"), milestone: HelpdeskWebhookDependencyMilestone }),
]);
export type HelpdeskWebhookDependencyMode = z.infer<typeof HelpdeskWebhookDependencyMode>;

export const HelpdeskWebhookResponsePermission = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("disabled") }).strict(),
  z.object({ mode: z.literal("all") }).strict(),
  z
    .object({
      mode: z.literal("selected"),
      ids: z
        .array(z.string().uuid())
        .min(1)
        .refine((ids) => new Set(ids).size === ids.length, "IDs must be unique"),
    })
    .strict(),
]);
export type HelpdeskWebhookResponsePermission = z.infer<typeof HelpdeskWebhookResponsePermission>;

export const HelpdeskWebhookResponseControl = z
  .object({
    linearDestinationId: z.string().uuid(),
    statuses: HelpdeskWebhookResponsePermission,
    labels: HelpdeskWebhookResponsePermission,
  })
  .strict()
  .refine((control) => control.statuses.mode !== "disabled" || control.labels.mode !== "disabled", "Enable at least one response control");
export type HelpdeskWebhookResponseControl = z.infer<typeof HelpdeskWebhookResponseControl>;

const HelpdeskWebhookResponseControls = z
  .array(HelpdeskWebhookResponseControl)
  .refine((controls) => new Set(controls.map((control) => control.linearDestinationId)).size === controls.length, "Response control destinations must be unique")
  .default([]);

export const HelpdeskWebhookOutcomeChanges = z
  .object({
    newStatus: z.string().uuid().optional(),
    addLabels: z
      .array(z.string().uuid())
      .refine((ids) => new Set(ids).size === ids.length, "Labels must be unique")
      .optional(),
    removeLabels: z
      .array(z.string().uuid())
      .refine((ids) => new Set(ids).size === ids.length, "Labels must be unique")
      .optional(),
  })
  .strict()
  .refine((change) => Boolean(change.newStatus || change.addLabels?.length || change.removeLabels?.length), "Choose at least one issue change")
  .refine((change) => !(change.addLabels ?? []).some((id) => change.removeLabels?.includes(id)), "A label cannot be added and removed together");
export type HelpdeskWebhookOutcomeChanges = z.infer<typeof HelpdeskWebhookOutcomeChanges>;

export const HelpdeskWebhookOutcomeHandling = z
  .object({
    retry: z.enum(["automatic", "stop"]).default("automatic"),
    failureTiming: z.enum(["first_failure", "after_retries"]).default("after_retries"),
    changes: z
      .array(
        z
          .object({
            linearDestinationId: z.string().uuid(),
            success: HelpdeskWebhookOutcomeChanges.optional(),
            failure: HelpdeskWebhookOutcomeChanges.optional(),
          })
          .strict()
          .refine((change) => Boolean(change.success || change.failure), "Choose success or failure changes"),
      )
      .refine((changes) => new Set(changes.map((change) => change.linearDestinationId)).size === changes.length, "Outcome destinations must be unique")
      .default([]),
  })
  .strict();
export type HelpdeskWebhookOutcomeHandling = z.infer<typeof HelpdeskWebhookOutcomeHandling>;

const helpdeskWebhookDependencyConfiguration = {
  outcomeHandling: HelpdeskWebhookOutcomeHandling.default({ retry: "automatic", failureTiming: "after_retries", changes: [] }),
  responseControls: HelpdeskWebhookResponseControls,
  linearDependencies: HelpdeskLinearDependencies,
  linearDependencyMode: HelpdeskWebhookDependencyMode.default({ mode: "selected" }),
};

const validateHelpdeskWebhookDependencyConfiguration = <Dependency>(
  value: {
    linearDependencies: readonly Dependency[];
    linearDependencyMode: HelpdeskWebhookDependencyMode;
    responseControls?: readonly HelpdeskWebhookResponseControl[];
    outcomeHandling?: HelpdeskWebhookOutcomeHandling;
  },
  ctx: z.RefinementCtx,
): void => {
  if (value.linearDependencyMode.mode === "selected") {
    const destinationIds = new Set(
      value.linearDependencies.map((dependency) => {
        const reference = dependency as { linearDestinationId?: string; sourceLinearDestinationId?: string };
        return reference.linearDestinationId ?? reference.sourceLinearDestinationId;
      }),
    );
    for (const [index, change] of (value.outcomeHandling?.changes ?? []).entries()) {
      if (!destinationIds.has(change.linearDestinationId))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Outcome changes must reference a selected Linear dependency", path: ["outcomeHandling", "changes", index, "linearDestinationId"] });
    }
    for (const [index, control] of (value.responseControls ?? []).entries()) {
      if (!destinationIds.has(control.linearDestinationId))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Response controls must reference a selected Linear dependency", path: ["responseControls", index, "linearDestinationId"] });
    }
  }
  for (const [index, change] of (value.outcomeHandling?.changes ?? []).entries()) {
    if (change.success && value.responseControls?.some((control) => control.linearDestinationId === change.linearDestinationId))
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Choose predefined or receiver-controlled success changes", path: ["outcomeHandling", "changes", index, "success"] });
  }
  for (const [index, dependency] of value.linearDependencies.entries()) {
    const {
      milestone,
      stateIds = [],
      anyTerminal = false,
    } = dependency as {
      milestone: HelpdeskWebhookDependencyMilestone;
      stateIds?: readonly string[];
      anyTerminal?: boolean;
    };
    const path = ["linearDependencies", index];
    if (isHelpdeskStatusMilestone(milestone)) {
      if (stateIds.length === 0 && !anyTerminal) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Choose at least one status", path: [...path, "stateIds"] });
      if (new Set(stateIds).size !== stateIds.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Statuses must be unique", path: [...path, "stateIds"] });
    } else if (stateIds.length > 0 || anyTerminal) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Statuses can only be selected for status dependencies", path: [...path, "stateIds"] });
    }
  }
  if (value.linearDependencyMode.mode === "all_routed" && value.linearDependencyMode.milestone === "status") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "All-routed dependencies cannot wait for a status", path: ["linearDependencyMode", "milestone"] });
  }
  if (value.linearDependencyMode.mode === "all_routed" && value.linearDependencies.length > 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "All-routed dependencies cannot also select individual Linear destinations", path: ["linearDependencies"] });
  }
};

// All-routed dependencies keep the existing `linearIssues` object contract. Each routed destination is keyed by
// its immutable destination UUID so conditionally routed subsets still produce deterministic payload field names.
export const helpdeskAllRoutedLinearIssueField = (linearDestinationId: string): string => `destination_${linearDestinationId.replaceAll("-", "_")}`;

export const HelpdeskWebhookDestination = z.object({
  ...helpdeskDestinationBase,
  kind: z.literal("webhook"),
  url: NonEmptyString,
  name: NonEmptyString,
  ...helpdeskWebhookDependencyConfiguration,
  responseControls: HelpdeskWebhookResponseControls.optional(),
  outcomeHandling: HelpdeskWebhookOutcomeHandling.optional(),
});
export type HelpdeskWebhookDestination = z.infer<typeof HelpdeskWebhookDestination>;

export const HelpdeskDestination = z.discriminatedUnion("kind", [HelpdeskLinearDestination, HelpdeskWebhookDestination]).superRefine((value, ctx) => {
  if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
});
export type HelpdeskDestination = z.infer<typeof HelpdeskDestination>;

const HelpdeskPortableSubcategory = z.object({ sourceSubcategoryId: z.string().uuid(), name: NonEmptyString.max(200) }).strict();

const HelpdeskPortableDepartment = z
  .object({
    sourceDepartmentId: z.string().uuid(),
    linearTeamId: NonEmptyString,
    name: NonEmptyString,
    category: z.boolean(),
    subcategory: HelpdeskPortableSubcategory.nullable(),
  })
  .strict();

const HelpdeskPortableLinearDestination = z
  .object({
    approvalInterop: HelpdeskApprovalInteropConfig.optional(),
    attachSubmittedFiles: z.boolean(),
    autoShareRequester: z.boolean(),
    documentSharingEnabled: z.boolean().optional(),
    coordinatorShareAndAssign: z.boolean().optional(),
    autoSubscribeRequester: z.boolean().optional(),
    statusEmailMode: HelpdeskStatusEmailMode.optional(),
    kind: z.literal("linear"),
    sourceId: z.string().uuid(),
    sourceDepartmentId: z.string().uuid(),
    linearTeamId: NonEmptyString,
    linearTeamName: NonEmptyString,
    linearProjectId: NonEmptyString.nullable(),
    communicationMode: HelpdeskCommunicationMode,
    titleTemplate: NonEmptyString.max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH),
    bodyTemplate: NonEmptyString.max(HELPDESK_BODY_TEMPLATE_MAX_LENGTH),
    issueFieldRules: z.array(HelpdeskIssueFieldRule),
    requesterCanShareWithOthers: z.boolean(),
    condition: HelpdeskDestinationCondition.nullable(),
  })
  .strict();

const HelpdeskPortableWebhookDependency = z.object({
  sourceLinearDestinationId: z.string().uuid(),
  field: HelpdeskLinearDependency.shape.field,
  milestone: HelpdeskWebhookDependencyMilestone,
  ...helpdeskStatusDependencyFields,
});

const HelpdeskPortableLinearDependencies = z
  .array(HelpdeskPortableWebhookDependency)
  .superRefine((dependencies, ctx) => refineUniqueHelpdeskDependencies(dependencies, (dependency) => dependency.sourceLinearDestinationId, "sourceLinearDestinationId", ctx));

const HelpdeskPortableWebhookDestination = z
  .object({
    kind: z.literal("webhook"),
    sourceId: z.string().uuid(),
    sourceDepartmentId: z.string().uuid(),
    name: NonEmptyString,
    url: z
      .string()
      .url()
      .max(2_000)
      .refine((value) => value.startsWith("https://") || value.startsWith("http://"), { message: "Webhook URL must use http or https" }),
    condition: HelpdeskDestinationCondition.nullable(),
    linearDependencies: HelpdeskPortableLinearDependencies,
    responseControls: HelpdeskWebhookResponseControls,
    outcomeHandling: HelpdeskWebhookOutcomeHandling.default({ retry: "automatic", failureTiming: "after_retries", changes: [] }),
    linearDependencyMode: HelpdeskWebhookDependencyMode,
  })
  .strict();

export const HelpdeskFormVersionExportDestination = z.discriminatedUnion("kind", [HelpdeskPortableLinearDestination, HelpdeskPortableWebhookDestination]).superRefine((value, ctx) => {
  if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
});
export type HelpdeskFormVersionExportDestination = z.infer<typeof HelpdeskFormVersionExportDestination>;

export const HelpdeskFormVersionExportManagedField = z
  .object({
    sourceId: z.string().uuid(),
    libraryName: NonEmptyString,
    type: HelpdeskManagedFieldType,
    scope: HelpdeskManagedFieldScope,
    owningDepartmentLinearTeamId: NonEmptyString.nullable(),
    versionId: z.string().uuid(),
    version: z.number().int().positive(),
    definition: HelpdeskManagedFieldDefinition,
  })
  .strict();
export type HelpdeskFormVersionExportManagedField = z.infer<typeof HelpdeskFormVersionExportManagedField>;

export const HelpdeskFormVersionExport = z
  .object({
    format: z.literal("helpdesk.form-version"),
    schemaVersion: z.literal(4),
    exportedAt: Timestamp,
    source: z
      .object({
        formId: z.string().uuid(),
        versionId: z.string().uuid(),
        state: HelpdeskFormVersionState,
        version: z.number().int().positive().nullable(),
        revision: z.number().int().nonnegative(),
      })
      .strict(),
    version: z
      .object({
        name: NonEmptyString,
        description: z.string().nullable(),
        requestTitleTemplate: z.string().trim().max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH).nullable().optional(),
        iconUrl: HelpdeskIconUrl.nullable(),
        portalSharingEnabled: z.boolean(),
        fields: z.array(HelpdeskFormFieldEntry),
        departments: z.array(HelpdeskPortableDepartment).min(1),
        destinations: z.array(HelpdeskFormVersionExportDestination),
      })
      .strict(),
    managedFields: z.array(HelpdeskFormVersionExportManagedField),
  })
  .strict();
export type HelpdeskFormVersionExport = z.infer<typeof HelpdeskFormVersionExport>;

// Keep the import schema compatible with legacy serialized records; current authored limits apply at the import boundary below.
const HelpdeskPortableWebhookDestinationForImport = HelpdeskPortableWebhookDestination.extend({ name: HelpdeskWebhookName });
const HelpdeskPortableLinearDestinationForLegacyImport = HelpdeskPortableLinearDestination.extend({
  attachSubmittedFiles: z.boolean().default(false),
  autoShareRequester: z.boolean().default(false),
  documentSharingEnabled: z.boolean().optional(),
  coordinatorShareAndAssign: z.boolean().optional(),
  autoSubscribeRequester: z.boolean().optional(),
  statusEmailMode: HelpdeskStatusEmailMode.optional(),
  requesterCanShareWithOthers: z.boolean().default(false),
});
const HelpdeskPortableLinearDestinationForV3Import = HelpdeskPortableLinearDestination.extend({
  autoShareRequester: z.boolean().default(false),
  documentSharingEnabled: z.boolean().optional(),
  coordinatorShareAndAssign: z.boolean().optional(),
  autoSubscribeRequester: z.boolean().optional(),
  statusEmailMode: HelpdeskStatusEmailMode.optional(),
  requesterCanShareWithOthers: z.boolean().default(false),
});
const HelpdeskFormVersionLegacyImportDestination = z
  .discriminatedUnion("kind", [HelpdeskPortableLinearDestinationForLegacyImport, HelpdeskPortableWebhookDestinationForImport])
  .superRefine((value, ctx) => {
    if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
  });
const HelpdeskFormVersionV3ImportDestination = z.discriminatedUnion("kind", [HelpdeskPortableLinearDestinationForV3Import, HelpdeskPortableWebhookDestinationForImport]).superRefine((value, ctx) => {
  if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
});
const HelpdeskFormVersionCurrentImportDestination = z.discriminatedUnion("kind", [HelpdeskPortableLinearDestination, HelpdeskPortableWebhookDestinationForImport]).superRefine((value, ctx) => {
  if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
});
const HelpdeskFormVersionImportVersion = HelpdeskFormVersionExport.shape.version.extend({ name: HelpdeskFormName, description: HelpdeskFormDescription }).strict();
const HelpdeskFormVersionLegacyImportVersion = HelpdeskFormVersionImportVersion.extend({
  destinations: z.array(HelpdeskFormVersionLegacyImportDestination),
  portalSharingEnabled: z.boolean().default(false),
}).strict();
const HelpdeskFormVersionV3ImportVersion = HelpdeskFormVersionImportVersion.extend({
  destinations: z.array(HelpdeskFormVersionV3ImportDestination),
  portalSharingEnabled: z.boolean().default(false),
}).strict();
const HelpdeskFormVersionCurrentImportVersion = HelpdeskFormVersionImportVersion.extend({ destinations: z.array(HelpdeskFormVersionCurrentImportDestination) }).strict();

export const HelpdeskFormVersionImport = z.discriminatedUnion("schemaVersion", [
  HelpdeskFormVersionExport.extend({ schemaVersion: z.literal(1), version: HelpdeskFormVersionLegacyImportVersion }).strict(),
  HelpdeskFormVersionExport.extend({ schemaVersion: z.literal(2), version: HelpdeskFormVersionLegacyImportVersion }).strict(),
  HelpdeskFormVersionExport.extend({ schemaVersion: z.literal(3), version: HelpdeskFormVersionV3ImportVersion }).strict(),
  HelpdeskFormVersionExport.extend({ schemaVersion: z.literal(4), version: HelpdeskFormVersionCurrentImportVersion }).strict(),
]);
export type HelpdeskFormVersionImport = z.infer<typeof HelpdeskFormVersionImport>;

export const HelpdeskFormTenantPlacement = z.object({
  tenantId: z.string().uuid(),
  departmentId: z.string().uuid(),
  subcategoryId: z.string().uuid().nullable(),
});
export type HelpdeskFormTenantPlacement = z.infer<typeof HelpdeskFormTenantPlacement>;

export const HelpdeskFormTenantExposure = z.object({
  tenantId: z.string().uuid(),
  generation: z.number().int().nonnegative(),
  enabled: z.boolean(),
});
export type HelpdeskFormTenantExposure = z.infer<typeof HelpdeskFormTenantExposure>;

const HelpdeskTenantIds = z
  .array(z.string().uuid())
  .min(1)
  .refine((ids) => new Set(ids).size === ids.length, "Tenant IDs must be unique");
export const HelpdeskCopyFormInput = z
  .object({
    sourceVersionId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    name: HelpdeskFormName,
    tenantIds: HelpdeskTenantIds,
    placements: z.array(HelpdeskFormTenantPlacement).min(1),
  })
  .strict();
export type HelpdeskCopyFormInput = z.infer<typeof HelpdeskCopyFormInput>;

export const HelpdeskCopyFormResult = z.object({ formId: z.string().uuid(), versionId: z.string().uuid() }).strict();
export type HelpdeskCopyFormResult = z.infer<typeof HelpdeskCopyFormResult>;

const HelpdeskFormImportMapping = z.record(z.string().uuid(), z.string().uuid());

export const HelpdeskFormVersionImportMappings = z.object({ departments: HelpdeskFormImportMapping, subcategories: HelpdeskFormImportMapping, managedFields: HelpdeskFormImportMapping }).strict();
export type HelpdeskFormVersionImportMappings = z.infer<typeof HelpdeskFormVersionImportMappings>;

export const HelpdeskFormVersionImportInput = z
  .object({
    bundle: HelpdeskFormVersionImport,
    mappings: HelpdeskFormVersionImportMappings,
    tenantIds: HelpdeskTenantIds.optional(),
    placements: z.array(HelpdeskFormTenantPlacement).optional(),
  })
  .strict();
export type HelpdeskFormVersionImportInput = z.infer<typeof HelpdeskFormVersionImportInput>;

export const HelpdeskFormVersionImportPreviewInput = z.object({ bundle: HelpdeskFormVersionImport }).strict();
export type HelpdeskFormVersionImportPreviewInput = z.infer<typeof HelpdeskFormVersionImportPreviewInput>;

const HelpdeskFormImportDepartmentCandidate = z.object({ id: z.string().uuid(), name: NonEmptyString, linearTeamId: NonEmptyString }).strict();
const HelpdeskFormImportSubcategoryCandidate = z.object({ id: z.string().uuid(), name: NonEmptyString.max(200), departmentId: z.string().uuid() }).strict();
const HelpdeskFormImportManagedFieldCandidate = z
  .object({
    id: z.string().uuid(),
    libraryName: NonEmptyString,
    type: HelpdeskManagedFieldType,
    scope: HelpdeskManagedFieldScope,
    owningDepartmentId: z.string().uuid().nullable(),
    owningDepartmentLinearTeamId: NonEmptyString.nullable(),
    owningDepartmentName: NonEmptyString.nullable(),
    publishedVersion: z.number().int().positive(),
  })
  .strict();

export const HelpdeskFormVersionImportPreview = z
  .object({
    bundle: HelpdeskFormVersionImport,
    departments: z.array(z.object({ source: HelpdeskPortableDepartment, candidates: z.array(HelpdeskFormImportDepartmentCandidate), suggestedId: z.string().uuid().nullable() }).strict()),
    subcategories: z.array(
      z
        .object({ sourceDepartmentId: z.string().uuid(), source: HelpdeskPortableSubcategory, candidates: z.array(HelpdeskFormImportSubcategoryCandidate), suggestedId: z.string().uuid().nullable() })
        .strict(),
    ),
    managedFields: z.array(
      z.object({ source: HelpdeskFormVersionExportManagedField, candidates: z.array(HelpdeskFormImportManagedFieldCandidate), suggestedId: z.string().uuid().nullable() }).strict(),
    ),
    errors: z.array(NonEmptyString),
  })
  .strict();
export type HelpdeskFormVersionImportPreview = z.infer<typeof HelpdeskFormVersionImportPreview>;

export const HelpdeskFormVersionImportResult = z.object({ formId: z.string().uuid(), versionId: z.string().uuid() }).strict();
export type HelpdeskFormVersionImportResult = z.infer<typeof HelpdeskFormVersionImportResult>;

const HelpdeskWebhookResponseNamedValue = z.object({ id: z.string().uuid(), name: z.string() }).strict();
export const HelpdeskWebhookResponseOptions = z.record(
  z.string(),
  z
    .object({
      currentStatus: HelpdeskWebhookResponseNamedValue.optional(),
      availableStatuses: z.array(HelpdeskWebhookResponseNamedValue).optional(),
      currentLabels: z.array(HelpdeskWebhookResponseNamedValue).optional(),
      availableLabels: z.array(HelpdeskWebhookResponseNamedValue.extend({ groupId: z.string().nullable().optional(), groupName: z.string().nullable().optional() })).optional(),
    })
    .strict(),
);
export type HelpdeskWebhookResponseOptions = z.infer<typeof HelpdeskWebhookResponseOptions>;

export const HelpdeskWebhookIssueUpdate = z
  .object({
    newStatus: z.string().uuid().optional(),
    addLabels: z.array(z.string().uuid()).optional(),
    removeLabels: z.array(z.string().uuid()).optional(),
  })
  .strict();
export type HelpdeskWebhookIssueUpdate = z.infer<typeof HelpdeskWebhookIssueUpdate>;
export const HelpdeskWebhookResponse = z.object({ issueUpdates: z.record(z.string(), HelpdeskWebhookIssueUpdate).optional() }).strict();
export type HelpdeskWebhookResponse = z.infer<typeof HelpdeskWebhookResponse>;

export const HelpdeskWebhookResponseCommand = z
  .object({
    key: z.string(),
    issueId: z.string().uuid(),
    teamId: z.string(),
    newStatus: z.string().uuid().optional(),
    addLabels: z.array(z.string().uuid()),
    removeLabels: z.array(z.string().uuid()),
  })
  .strict();
export type HelpdeskWebhookResponseCommand = z.infer<typeof HelpdeskWebhookResponseCommand>;

export const HelpdeskWebhookPayload = z.object({
  requestId: z.string().uuid().optional(),
  form: z.object({ id: z.string().uuid(), name: z.string() }),
  department: z.object({ id: z.string().uuid(), name: z.string() }),
  requester: z.union([
    z.object({ name: z.string(), email: z.string().email(), entraId: z.string() }),
    z.object({ name: z.string(), email: z.string().email(), entraId: z.null(), portalUserId: z.string().trim().min(1).max(256) }),
    z.object({ name: z.string(), email: z.string().email(), entraId: z.null(), connectedAppId: z.string().uuid() }),
  ]),
  answers: z.array(HelpdeskWebhookAnswer),
  linearIssues: z.record(z.string(), z.string().uuid()).optional(),
  approvalResult: z
    .record(
      z.string(),
      z.object({
        decision: z.enum(["approved", "denied"]),
        actor: z.object({ entraId: z.string(), name: z.string().nullable(), email: z.string().nullable() }),
        reason: z.string().nullable(),
        finalizedAt: z.string().datetime(),
      }),
    )
    .optional(),
  linearStatus: z.record(z.string(), z.object({ stateId: z.string(), name: z.string(), type: z.string(), enteredAt: z.string().datetime() })).optional(),
  responseOptions: HelpdeskWebhookResponseOptions.optional(),
  submittedAt: z.string().datetime(),
});
export type HelpdeskWebhookPayload = z.infer<typeof HelpdeskWebhookPayload>;

export const HelpdeskFormActions = z.object({
  copy: z.boolean(),
  withdraw: z.boolean(),
  archive: z.boolean(),
  createDraft: z.boolean(),
  discardDraft: z.boolean(),
  editShared: z.boolean(),
  exportJson: z.boolean(),
  markReady: z.boolean(),
  // Computed against the complete draft, including Departments and destinations hidden from this caller.
  publish: z.boolean(),
  restore: z.boolean(),
  setVisibility: z.boolean(),
});
export type HelpdeskFormActions = z.infer<typeof HelpdeskFormActions>;

// Publication blockers intentionally carry only an aggregate code and count. The authoring projection can hide
// Departments and destinations from a collaborator, so returning their identities here would disclose private
// configuration while still not giving the caller enough information to act on it.
export const HelpdeskPublicationBlockerCode = z.enum([
  "no_destinations",
  "missing_category",
  "missing_catch_all_destination",
  "inactive_departments",
  "departments_not_ready",
  "invalid_destinations",
  "preview_required",
  "plugin_unavailable",
  "plugin_config_invalid",
  "plugin_semantic_invalid",
  "plugin_control_mismatch",
  "tenant_eligibility",
  "tenant_withdrawn",
  "external_fields_unsupported",
]);
export type HelpdeskPublicationBlockerCode = z.infer<typeof HelpdeskPublicationBlockerCode>;

export const HelpdeskPublicationBlocker = z.object({ code: HelpdeskPublicationBlockerCode, count: z.number().int().positive() }).strict();
export type HelpdeskPublicationBlocker = z.infer<typeof HelpdeskPublicationBlocker>;

export const HelpdeskAuthoringVersion = z.object({
  tenantIds: z.array(z.string().uuid()),
  withdrawnTenantIds: z.array(z.string().uuid()).optional(),
  placements: z.array(HelpdeskFormTenantPlacement),
  tenantExposures: z.array(HelpdeskFormTenantExposure),
  id: z.string().uuid(),
  formId: z.string().uuid(),
  state: HelpdeskFormVersionState,
  version: z.number().int().positive().nullable(),
  revision: z.number().int().nonnegative(),
  name: NonEmptyString,
  description: z.string().trim().min(1).nullable(),
  requestTitleTemplate: z.string().trim().max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH).nullable().optional(),
  iconUrl: HelpdeskIconUrl.nullable().optional(),
  portalSharingEnabled: z.boolean(),
  fields: z.array(HelpdeskFormFieldEntry),
  pluginDescriptors: z.array(HelpdeskPluginAuthoringDescriptor),
  resolvedFields: z.array(HelpdeskFormField),
  departments: z.array(HelpdeskDraftDepartment).min(1),
  destinations: z.array(HelpdeskDestination),
  createdBy: HelpdeskActor,
  updatedBy: HelpdeskActor,
  restoredFromVersion: z.number().int().positive().nullable(),
  publishedAt: Timestamp.nullable(),
  publishedBy: HelpdeskActor.nullable(),
  previewAcknowledgement: HelpdeskPreviewAcknowledgement.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type HelpdeskAuthoringVersion = z.infer<typeof HelpdeskAuthoringVersion>;

export const HelpdeskAuthoringFormSummary = z.object({
  hidden: z.boolean(),
  id: z.string().uuid(),
  name: NonEmptyString,
  state: z.enum(["draft", "published", "archived"]),
  departments: z.array(HelpdeskDepartmentRef.extend({ category: z.boolean() })).min(1),
  updatedAt: Timestamp,
});
export type HelpdeskAuthoringFormSummary = z.infer<typeof HelpdeskAuthoringFormSummary>;

export const HelpdeskAuthoringFormsResponse = z.object({
  availableTenants: z.array(HelpdeskTenant),
  forms: z.array(HelpdeskAuthoringFormSummary),
  manageableDepartments: z.array(HelpdeskDepartment),
  availableDepartments: z.array(HelpdeskDepartmentRef),
  canImportJson: z.boolean(),
  departmentGroups: z.array(HelpdeskDepartmentGroupDirectoryEntry),
});
export type HelpdeskAuthoringFormsResponse = z.infer<typeof HelpdeskAuthoringFormsResponse>;

export const HelpdeskAuthoringForm = z.object({
  hidden: z.boolean(),
  id: z.string().uuid(),
  archivedAt: Timestamp.nullable(),
  archivedBy: HelpdeskActor.nullable(),
  draftDiagnostics: z.object({ unusedFieldIds: z.array(z.string().uuid()) }).nullable(),
  draft: HelpdeskAuthoringVersion.nullable(),
  published: HelpdeskAuthoringVersion.nullable(),
  history: z.array(
    z.object({
      tenantIds: z.array(z.string().uuid()),
      placements: z.array(HelpdeskFormTenantPlacement),
      createdAt: Timestamp,
      createdBy: HelpdeskActor,
      id: z.string().uuid(),
      version: z.number().int().positive(),
      state: HelpdeskFormVersionState.extract(["published", "superseded"]),
      name: NonEmptyString,
      departments: z
        .array(
          z.object({
            department: HelpdeskDepartmentRef,
            readyAt: Timestamp.nullable(),
            readyBy: HelpdeskActor.nullable(),
          }),
        )
        .min(1),
      publishedAt: Timestamp,
      publishedBy: HelpdeskActor,
    }),
  ),
  managedFields: z.array(HelpdeskManagedFieldResolution),
  publicationBlockers: z.array(HelpdeskPublicationBlocker),
  _actions: HelpdeskFormActions,
});
export type HelpdeskAuthoringForm = z.infer<typeof HelpdeskAuthoringForm>;

export const HelpdeskAuthoringFormResponse = z.object({ form: HelpdeskAuthoringForm });
export type HelpdeskAuthoringFormResponse = z.infer<typeof HelpdeskAuthoringFormResponse>;
export const HelpdeskAuthoringMutationResponse = HelpdeskAuthoringFormResponse.extend({ appliedVersion: HelpdeskVersionToken.nullable() });
export type HelpdeskAuthoringMutationResponse = z.infer<typeof HelpdeskAuthoringMutationResponse>;

export const CreateHelpdeskFormInput = z.object({
  name: HelpdeskFormName,
  departmentId: z.string().uuid(),
  tenantIds: HelpdeskTenantIds.optional(),
  placements: z.array(HelpdeskFormTenantPlacement).optional(),
});
export type CreateHelpdeskFormInput = z.infer<typeof CreateHelpdeskFormInput>;

export const HelpdeskRevisionInput = z.object({ expectedVersionId: z.string().uuid(), revision: z.number().int().nonnegative() });
export type HelpdeskRevisionInput = z.infer<typeof HelpdeskRevisionInput>;

export const SetHelpdeskFormVisibilityInput = z.object({ hidden: z.boolean() }).strict();
export type SetHelpdeskFormVisibilityInput = z.infer<typeof SetHelpdeskFormVisibilityInput>;

export const MarkHelpdeskDepartmentReadyInput = HelpdeskRevisionInput.extend({ departmentId: z.string().uuid() });
export type MarkHelpdeskDepartmentReadyInput = z.infer<typeof MarkHelpdeskDepartmentReadyInput>;

export const SaveHelpdeskSharedDefinitionInput = HelpdeskRevisionInput.extend({
  name: HelpdeskFormName,
  description: HelpdeskFormDescription,
  iconUrl: HelpdeskIconUrl.nullable(),
  requestTitleTemplate: z.string().trim().max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH).nullable().optional(),
  portalSharingEnabled: z.boolean().default(false),
  fields: z.array(HelpdeskFormFieldEntryInput),
});

export const SaveHelpdeskDepartmentIconInput = z.object({
  iconUrl: HelpdeskIconUrl.nullable(),
});
export type SaveHelpdeskDepartmentIconInput = z.infer<typeof SaveHelpdeskDepartmentIconInput>;
export const SaveHelpdeskDepartmentDescriptionInput = z.object({ description: HelpdeskDepartmentDescription });
export type SaveHelpdeskDepartmentDescriptionInput = z.infer<typeof SaveHelpdeskDepartmentDescriptionInput>;
export type SaveHelpdeskSharedDefinitionInput = z.infer<typeof SaveHelpdeskSharedDefinitionInput>;

export const SaveHelpdeskCollaborationInput = HelpdeskRevisionInput.extend({
  departments: z
    .array(
      z.object({
        departmentId: z.string().uuid(),
        category: z.boolean(),
        subcategoryId: z.string().uuid().nullable().optional(),
      }),
    )
    .min(1),
});
export type SaveHelpdeskCollaborationInput = z.infer<typeof SaveHelpdeskCollaborationInput>;

export const SaveHelpdeskAudienceInput = HelpdeskRevisionInput.extend({
  tenantIds: HelpdeskTenantIds,
  placements: z.array(HelpdeskFormTenantPlacement).min(1),
  departments: SaveHelpdeskCollaborationInput.shape.departments.optional(),
  readdTenantIds: z.array(z.string().uuid()).optional(),
}).superRefine((input, context) => {
  const tenants = new Set(input.tenantIds);
  const placements = new Set<string>();
  for (const placement of input.placements) {
    const key = `${placement.tenantId}:${placement.departmentId}`;
    if (!tenants.has(placement.tenantId) || placements.has(key)) context.addIssue({ code: "custom", message: "Invalid tenant placement", path: ["placements"] });
    placements.add(key);
  }
  if (input.tenantIds.some((tenantId) => !input.placements.some((placement) => placement.tenantId === tenantId))) {
    context.addIssue({ code: "custom", message: "Each tenant requires a category", path: ["placements"] });
  }
  if (input.readdTenantIds?.some((tenantId) => !tenants.has(tenantId))) context.addIssue({ code: "custom", message: "Re-added tenants must be selected", path: ["readdTenantIds"] });
});
export type SaveHelpdeskAudienceInput = z.infer<typeof SaveHelpdeskAudienceInput>;

export const SaveHelpdeskLinearDestinationInput = HelpdeskRevisionInput.extend({
  approvalInterop: HelpdeskApprovalInteropConfig.optional(),
  kind: z.literal("linear"),
  attachSubmittedFiles: z.boolean().default(false),
  autoShareRequester: z.boolean().default(false),
  documentSharingEnabled: z.boolean().default(false),
  coordinatorShareAndAssign: z.boolean().default(false),
  autoSubscribeRequester: z.boolean().optional(),
  statusEmailMode: HelpdeskStatusEmailMode.optional(),
  departmentId: z.string().uuid(),
  linearTeamId: NonEmptyString,
  linearProjectId: NonEmptyString.nullable(),
  communicationMode: HelpdeskCommunicationMode,
  titleTemplate: NonEmptyString.max(HELPDESK_TITLE_TEMPLATE_MAX_LENGTH),
  bodyTemplate: NonEmptyString.max(HELPDESK_BODY_TEMPLATE_MAX_LENGTH),
  issueFieldRules: z.array(HelpdeskIssueFieldRule).default([]),
  requesterCanShareWithOthers: z.boolean().default(false),
  condition: HelpdeskDestinationCondition.nullable().optional(),
});
export type SaveHelpdeskLinearDestinationInput = z.infer<typeof SaveHelpdeskLinearDestinationInput>;

// Transport policy is enforced by the server because development may admit one exact loopback harness URL. Keep the
// wire contract limited to HTTP(S); production and every unconfigured deployment still accept HTTPS only.
export const HelpdeskWebhookUrl = z
  .string()
  .url()
  .max(2_000)
  .refine((value) => value.startsWith("https://") || value.startsWith("http://"), { message: "Webhook URL must use http or https" });
export type HelpdeskWebhookUrl = z.infer<typeof HelpdeskWebhookUrl>;

export const SaveHelpdeskWebhookDestinationInput = HelpdeskRevisionInput.extend({
  kind: z.literal("webhook"),
  departmentId: z.string().uuid(),
  url: HelpdeskWebhookUrl,
  name: HelpdeskWebhookName,
  condition: HelpdeskDestinationCondition.nullable().optional(),
  ...helpdeskWebhookDependencyConfiguration,
});
export type SaveHelpdeskWebhookDestinationInput = z.infer<typeof SaveHelpdeskWebhookDestinationInput>;

export const SaveHelpdeskDestinationInput = z.discriminatedUnion("kind", [SaveHelpdeskLinearDestinationInput, SaveHelpdeskWebhookDestinationInput]).superRefine((value, ctx) => {
  if (value.kind === "webhook") validateHelpdeskWebhookDependencyConfiguration(value, ctx);
});
export type SaveHelpdeskDestinationInput = z.infer<typeof SaveHelpdeskDestinationInput>;

// Pre-save test send: the author supplies the department (for authorization and the sample payload's Department ref)
// and the currently-typed URL. Fields and form identity come from the draft server-side, never the client.
export const HelpdeskWebhookTestInput = z
  .object({
    departmentId: z.string().uuid(),
    ...helpdeskWebhookDependencyConfiguration,
    url: HelpdeskWebhookUrl,
  })
  .superRefine(validateHelpdeskWebhookDependencyConfiguration);
export type HelpdeskWebhookTestInput = Omit<z.infer<typeof HelpdeskWebhookTestInput>, "responseControls" | "outcomeHandling"> & {
  responseControls?: HelpdeskWebhookResponseControl[];
  outcomeHandling?: HelpdeskWebhookOutcomeHandling;
};

// Outcome of a test send. "not_configured" means the deployment has no signing secret, so a real (signed) delivery
// would not run either; the UI surfaces it as an ask-IT-Admin message rather than a network result.
export const HelpdeskWebhookTestResult = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("delivered"), status: z.number().int() }),
  z.object({ outcome: z.literal("invalid_response"), status: z.number().int() }),
  z.object({ outcome: z.literal("endpoint_error"), status: z.number().int() }),
  z.object({ outcome: z.literal("unreachable") }),
  z.object({ outcome: z.literal("not_configured") }),
]);
export type HelpdeskWebhookTestResult = z.infer<typeof HelpdeskWebhookTestResult>;

export const RestoreHelpdeskFormInput = HelpdeskRevisionInput.extend({
  sourceVersionId: z.string().uuid(),
});
export type RestoreHelpdeskFormInput = z.infer<typeof RestoreHelpdeskFormInput>;

export const HelpdeskDepartmentArchiveImpact = z.object({
  exposedForms: z.array(z.object({ formId: z.string().uuid(), tenantId: z.string().uuid(), name: z.string() })).optional(),
  draftForms: z.array(z.object({ formId: z.string().uuid(), tenantId: z.string().uuid(), name: z.string() })).optional(),
  categoryListingsRemoved: z.number().int().nonnegative(),
  formsBecomingUnavailable: z.number().int().nonnegative(),
  draftsRequiringCleanup: z.number().int().nonnegative(),
});
export type HelpdeskDepartmentArchiveImpact = z.infer<typeof HelpdeskDepartmentArchiveImpact>;

export const HelpdeskDepartmentArchiveImpactResponse = z.object({ impact: HelpdeskDepartmentArchiveImpact });
export type HelpdeskDepartmentArchiveImpactResponse = z.infer<typeof HelpdeskDepartmentArchiveImpactResponse>;

export const CreateHelpdeskRequestInput = z.object({
  formVersionId: z.string().uuid(),
  idempotencyKey: z.string().uuid(),
  linearShareUserIds: z
    .array(NonEmptyString)
    .max(HELPDESK_LINEAR_SHARE_RECIPIENT_MAX)
    .refine((ids) => new Set(ids).size === ids.length, "Linear user ids must be unique")
    .default([]),
  managedFieldVersionToken: NonEmptyString,
  requesterTimeZone: z.string().refine((timeZone) => {
    try {
      Intl.DateTimeFormat(undefined, { timeZone });
      return true;
    } catch {
      return false;
    }
  }, "requesterTimeZone must be an IANA time zone"),
  values: z.record(z.string().uuid(), HelpdeskFormInputValue),
});
export type CreateHelpdeskRequestInput = z.infer<typeof CreateHelpdeskRequestInput>;

export const HelpdeskRequestBucket = z.enum(["open", "done"]);
export type HelpdeskRequestBucket = z.infer<typeof HelpdeskRequestBucket>;

export const HelpdeskRequestRelationship = z.enum(["requester", "shared"]);
export type HelpdeskRequestRelationship = z.infer<typeof HelpdeskRequestRelationship>;

export const HelpdeskRequestRelationshipFilter = z.enum(["all", "requested_by_me", "shared_with_me"]);
export type HelpdeskRequestRelationshipFilter = z.infer<typeof HelpdeskRequestRelationshipFilter>;

export const HelpdeskRequestStatus = z.enum([
  "preparing_issues",
  "manual_retry_needed",
  "creation_failed",
  "not_started",
  "in_progress",
  "not_done",
  "done",
  "cancellation_pending",
  "cancelled",
  "cancellation_needs_attention",
]);
export type HelpdeskRequestStatus = z.infer<typeof HelpdeskRequestStatus>;

const AGGREGATE_HELPDESK_REQUEST_STATUSES = new Set<HelpdeskRequestStatus>(["not_started", "in_progress", "not_done", "done"]);
const NOT_STARTED_LINEAR_STATE_TYPES: ReadonlySet<string> = new Set(["backlog", "triage", "unstarted"]);

export const HELPDESK_TERMINAL_LINEAR_STATE_TYPES: readonly string[] = ["completed", "canceled", "duplicate"];

export function isHelpdeskTerminalLinearStateType(stateType: string): boolean {
  return HELPDESK_TERMINAL_LINEAR_STATE_TYPES.includes(stateType);
}

export type HelpdeskAggregateIssueState = {
  bucket: HelpdeskRequestBucket;
  linearStateType: string;
  removed?: boolean;
};

/**
 * The single definition of how a Request's delivered children roll up into the requester-facing
 * status and the coarse Open/Done bucket. The server derives every Request's presentation with it
 * and the contracts below validate against it, so the rule can only be read or changed in one
 * place. Cancellation or duplicate closure wins over completion: a terminal ticket does not
 * necessarily mean its work was completed. An unknown state type counts as started rather than
 * falsely claiming that no work has begun.
 *
 * Callers must pass one issue per delivered target; a Request with an undelivered target is
 * creation-incomplete and never reaches this rule.
 */
export const aggregateHelpdeskRequestPresentation = (issues: readonly HelpdeskAggregateIssueState[]): { bucket: HelpdeskRequestBucket; status: HelpdeskRequestStatus } => {
  const bucket: HelpdeskRequestBucket = issues.every((issue) => issue.bucket === "done") ? "done" : "open";
  if (issues.some((issue) => issue.removed === true || issue.linearStateType === "canceled" || issue.linearStateType === "duplicate")) return { bucket, status: "not_done" };
  if (issues.every((issue) => issue.linearStateType === "completed")) return { bucket, status: "done" };
  if (issues.every((issue) => NOT_STARTED_LINEAR_STATE_TYPES.has(issue.linearStateType))) return { bucket, status: "not_started" };
  return { bucket, status: "in_progress" };
};

export const HelpdeskRequestTargetState = z.enum(["queued", "failed", "expired", "delivered"]);
export type HelpdeskRequestTargetState = z.infer<typeof HelpdeskRequestTargetState>;

export const HelpdeskLinearStateProjection = z.object({
  id: NonEmptyString,
  name: NonEmptyString,
  type: NonEmptyString,
});
export type HelpdeskLinearStateProjection = z.infer<typeof HelpdeskLinearStateProjection>;

export const HelpdeskAssigneeProjection = z.object({ name: NonEmptyString });
export type HelpdeskAssigneeProjection = z.infer<typeof HelpdeskAssigneeProjection>;

export const HelpdeskIssueProjection = z
  .object({
    id: NonEmptyString,
    identifier: NonEmptyString,
    title: NonEmptyString,
    state: HelpdeskLinearStateProjection,
    assignee: HelpdeskAssigneeProjection.nullable(),
    bucket: HelpdeskRequestBucket,
    removedInLinear: z.boolean().optional(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .superRefine((issue, ctx) => {
    const terminal = isHelpdeskTerminalLinearStateType(issue.state.type);
    const expectedBucket = issue.removedInLinear || terminal ? "done" : "open";
    if (issue.bucket !== expectedBucket) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bucket"], message: "issue bucket must match the Linear state type" });
    }
    if (issue.removedInLinear && terminal) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["removedInLinear"], message: "only non-terminal issues present as removed" });
    }
  });
export type HelpdeskIssueProjection = z.infer<typeof HelpdeskIssueProjection>;

const HelpdeskRequestIdentity = {
  id: z.string().uuid(),
  formVersionId: z.string().uuid(),
  formName: NonEmptyString,
  title: NonEmptyString.nullable().optional(),
  relationship: HelpdeskRequestRelationship,
  requester: z.object({ name: NonEmptyString }).strict(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
};

const HelpdeskRequestTargetSummaryBase = z.object({
  id: z.string().uuid(),
  departmentName: NonEmptyString,
  ticketNumber: z.number().int().positive(),
  hasUnreadReplies: z.boolean().nullable().optional(),
  cancelled: z.boolean().optional(),
  state: HelpdeskRequestTargetState,
  canRetry: z.boolean(),
  issue: HelpdeskIssueProjection.nullable(),
});

const refineHelpdeskRequestTarget = (target: z.infer<typeof HelpdeskRequestTargetSummaryBase>, ctx: z.RefinementCtx): void => {
  if ((target.state === "delivered") !== (target.issue !== null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["issue"], message: "only delivered targets have an issue projection" });
  }
  if (target.cancelled && (target.state !== "queued" || target.issue !== null || target.canRetry)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cancelled"], message: "cancelled targets without an issue cannot be retried" });
  }
  if (target.canRetry && target.state !== "failed") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["canRetry"], message: "only failed targets can be retryable" });
  }
};

export const HelpdeskRequestTargetSummary = HelpdeskRequestTargetSummaryBase.superRefine(refineHelpdeskRequestTarget);
export type HelpdeskRequestTargetSummary = z.infer<typeof HelpdeskRequestTargetSummary>;

type HelpdeskRequestAggregationInput = {
  bucket: HelpdeskRequestBucket;
  canRetry: boolean;
  relationship: HelpdeskRequestRelationship;
  status: HelpdeskRequestStatus;
  targets: z.infer<typeof HelpdeskRequestTargetSummaryBase>[];
  cancellation?: { status: "pending" | "confirmed" | "needs_attention" } | null;
};

const refineHelpdeskRequestAggregation = (request: HelpdeskRequestAggregationInput, ctx: z.RefinementCtx): void => {
  if (request.cancellation) {
    const status = request.cancellation.status === "confirmed" ? "cancelled" : request.cancellation.status === "pending" ? "cancellation_pending" : "cancellation_needs_attention";
    const bucket = request.cancellation.status === "confirmed" ? "done" : "open";
    const confirmedTargets = request.targets.every((target) => (target.issue ? target.issue.state.type === "canceled" : target.cancelled === true));
    if (
      request.status !== status ||
      request.bucket !== bucket ||
      request.canRetry ||
      request.targets.some((target) => target.canRetry) ||
      (request.cancellation.status === "confirmed" && !confirmedTargets)
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "cancellation presentation must match its progress and disable retries" });
    }
    return;
  }
  if (request.targets.some((target) => target.cancelled)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["targets"], message: "cancelled targets require an accepted cancellation" });
  }
  if (request.status === "cancelled" || request.status === "cancellation_pending" || request.status === "cancellation_needs_attention") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["status"], message: "cancellation status requires an accepted operation" });
  }
  const canRetry = request.status === "manual_retry_needed";
  const issues = request.targets.flatMap((target) => (target.issue ? [target.issue] : []));
  // A Request whose targets have not all delivered is creation-incomplete: the aggregate rule does
  // not apply to it, and it stays Open whatever its already-created children look like.
  const aggregate =
    issues.length === request.targets.length
      ? aggregateHelpdeskRequestPresentation(issues.map((issue) => ({ bucket: issue.bucket, linearStateType: issue.state.type, removed: issue.removedInLinear === true })))
      : null;

  const hasRetryableTarget = request.targets.some((target) => target.canRetry);
  const retryVisibilityIsValid = request.relationship === "requester" ? request.canRetry === canRetry && hasRetryableTarget === canRetry : !request.canRetry && !hasRetryableTarget;
  if (!retryVisibilityIsValid) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["canRetry"], message: "canRetry must match the request status" });
  }
  if (request.bucket !== (aggregate?.bucket ?? "open")) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["bucket"], message: "request bucket must match whether all targets are terminal" });
  }
  if (AGGREGATE_HELPDESK_REQUEST_STATUSES.has(request.status) && request.status !== aggregate?.status) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["status"], message: "request status must match the aggregate Linear states" });
  }
};

export const HelpdeskRequestCancellation = z.object({
  operationId: z.string().uuid(),
  requestedAt: Timestamp,
  status: z.enum(["pending", "confirmed", "needs_attention"]),
});
export type HelpdeskRequestCancellation = z.infer<typeof HelpdeskRequestCancellation>;

export function aggregateHelpdeskCancellation(
  operationId: string | null,
  requestedAt: Date | null,
  targets: readonly { state: string; cancellationState: string | null; issueId: string | null; linearStateType: string | null }[],
): HelpdeskRequestCancellation | null {
  if (!operationId || !requestedAt) return null;
  const attention = targets.some(
    (target) =>
      target.cancellationState === "needs_attention" ||
      (target.cancellationState === "confirmed" && ((target.state === "delivered" && target.issueId === null) || (target.issueId !== null && target.linearStateType !== "canceled"))),
  );
  return {
    operationId,
    requestedAt: requestedAt.toISOString(),
    status: attention ? "needs_attention" : targets.length > 0 && targets.every((target) => target.cancellationState === "confirmed") ? "confirmed" : "pending",
  };
}

export const HelpdeskRequestSummary = z
  .object({
    ...HelpdeskRequestIdentity,
    hasUnreadReplies: z.boolean().nullable().optional(),
    needsAttention: z.boolean().default(false),
    status: HelpdeskRequestStatus,
    bucket: HelpdeskRequestBucket,
    cancellation: HelpdeskRequestCancellation.nullable().optional(),
    targets: z.array(HelpdeskRequestTargetSummary).min(1),
    canRetry: z.boolean(),
  })
  .superRefine(refineHelpdeskRequestAggregation);
export type HelpdeskRequestSummary = z.infer<typeof HelpdeskRequestSummary>;

export const HelpdeskRequestLiveContent = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    description: z.string(),
    attachments: z.array(HelpdeskAttachmentMedia).default([]),
  }),
  z.object({
    status: z.literal("unavailable"),
  }),
]);
export type HelpdeskRequestLiveContent = z.infer<typeof HelpdeskRequestLiveContent>;

export const HelpdeskRequestApproval = z.discriminatedUnion("stage", [
  z.object({ stage: z.literal("awaiting_assignment") }),
  z.object({ stage: z.literal("awaiting_decision"), approverNames: z.array(z.string()), reviewUrl: z.string().url() }),
  z.object({ stage: z.literal("pending_confirmation") }),
  z.object({ stage: z.literal("unavailable") }),
]);
export type HelpdeskRequestApproval = z.infer<typeof HelpdeskRequestApproval>;
export type HelpdeskApprovalBinding = { linearIssueId: string; linearTeamId: string };
export type HelpdeskApprovalSummary = HelpdeskApprovalBinding & { approval: HelpdeskRequestApproval };
export type HelpdeskApprovalLookup = (bindings: readonly HelpdeskApprovalBinding[]) => Promise<readonly HelpdeskApprovalSummary[]>;

export const HelpdeskWebhookOutcomeError = z
  .object({
    name: z.string(),
    referenceId: z.string(),
    partiallyApplied: z.boolean(),
    issueUpdateFailed: z.boolean().optional(),
  })
  .strict();
export type HelpdeskWebhookOutcomeError = z.infer<typeof HelpdeskWebhookOutcomeError>;

export const HELPDESK_LINEAR_ISSUE_URL_PREFIX = "https://linear.app/";
export const HelpdeskRequestTarget = HelpdeskRequestTargetSummaryBase.extend({
  automationAttention: z.boolean().default(false),
  automationAttentionCursor: z.string().uuid().nullable().optional(),
  approval: HelpdeskRequestApproval.nullable().optional(),
  canComment: z.boolean(),
  communicationMode: HelpdeskCommunicationMode,
  liveContent: HelpdeskRequestLiveContent.nullable(),
  linearIssueUrl: z.string().url().startsWith(HELPDESK_LINEAR_ISSUE_URL_PREFIX).optional(),
}).superRefine((target, ctx) => {
  refineHelpdeskRequestTarget(target, ctx);
  if ((target.state === "delivered") !== (target.liveContent !== null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["liveContent"], message: "only delivered targets have live-content availability" });
  }
});
export type HelpdeskRequestTarget = z.infer<typeof HelpdeskRequestTarget>;

export const HELPDESK_WEBHOOK_REPLAY_WINDOW_MS = 7 * 24 * 60 * 60_000;

export const HelpdeskWebhookDeliverySummary = z.object({
  id: z.string().uuid(),
  name: NonEmptyString,
  status: z.enum(["waiting_for_approval", "waiting_for_status", "queued", "sending", "retrying", "failed", "delivered", "not_reached", "unavailable"]),
  canRetrigger: z.boolean(),
  replayGeneration: z.number().int().nonnegative(),
  replayExpiresAt: Timestamp.nullable(),
  deliveredAt: Timestamp.nullable(),
  lastRetriggerAt: Timestamp.nullable(),
  lastRetriggerBy: z.string().nullable(),
});
export type HelpdeskWebhookDeliverySummary = z.infer<typeof HelpdeskWebhookDeliverySummary>;

export const RetriggerHelpdeskWebhookInput = z.object({ expectedGeneration: z.number().int().nonnegative() }).strict();
export type RetriggerHelpdeskWebhookInput = z.infer<typeof RetriggerHelpdeskWebhookInput>;
export type RetriggerHelpdeskWebhookResult = "queued" | "not_found" | "not_retriggerable";

// Approval routes use this port through the composition root, without reaching into Helpdesk storage.
export type HelpdeskApprovalWebhookRecovery = {
  list: (approvalRequestId: string, actorEntraId: string, now: Date) => Promise<HelpdeskWebhookDeliverySummary[]>;
  retrigger: (approvalRequestId: string, deliveryId: string, actor: HelpdeskActor, expectedGeneration: number, now: Date) => Promise<RetriggerHelpdeskWebhookResult>;
};

export const HelpdeskRequestDetail = z
  .object({
    submittedAttachments: z.array(HelpdeskSubmittedAttachment).max(HELPDESK_MAX_ATTACHMENT_REFERENCES).nullable().optional(),
    ...HelpdeskRequestIdentity,
    hasUnreadReplies: z.boolean().nullable().optional(),
    status: HelpdeskRequestStatus,
    bucket: HelpdeskRequestBucket,
    cancellation: HelpdeskRequestCancellation.nullable().optional(),
    canManageSharing: z.boolean(),
    canCancel: z.boolean().optional(),
    canRetry: z.boolean(),
    targets: z.array(HelpdeskRequestTarget).min(1),
  })
  .superRefine(refineHelpdeskRequestAggregation);
export type HelpdeskRequestDetail = z.infer<typeof HelpdeskRequestDetail>;

export const HelpdeskCommentAuthor = z
  .object({
    isCurrentUser: z.boolean(),
    kind: z.enum(["requester", "shared_portal_user", "linear_user"]),
    name: NonEmptyString,
  })
  .strict();
export type HelpdeskCommentAuthor = z.infer<typeof HelpdeskCommentAuthor>;

export const HelpdeskComment = z.object({
  id: NonEmptyString,
  author: HelpdeskCommentAuthor,
  body: z.string(),
  attachments: z.array(HelpdeskAttachmentMedia).default([]),
  createdAt: Timestamp,
  isUnread: z.boolean().optional(),
});
export type HelpdeskComment = z.infer<typeof HelpdeskComment>;

export const HelpdeskRequestPage = z.object({
  items: z.array(HelpdeskRequestSummary),
  nextCursor: NonEmptyString.nullable(),
});
export type HelpdeskRequestPage = z.infer<typeof HelpdeskRequestPage>;

export const HelpdeskReadCursor = z
  .object({
    createdAt: Timestamp,
    commentIds: z
      .array(NonEmptyString)
      .min(1)
      .max(50)
      .refine((ids) => new Set(ids).size === ids.length),
  })
  .strict();
export type HelpdeskReadCursor = z.infer<typeof HelpdeskReadCursor>;
export const PutHelpdeskReadInput = z.object({ readCursor: HelpdeskReadCursor }).strict();
export const PutHelpdeskAttentionReadInput = z.object({ episodeId: z.string().uuid() }).strict();
export type PutHelpdeskReadInput = z.infer<typeof PutHelpdeskReadInput>;
export const HelpdeskRequestActivity = z
  .object({
    requestId: z.string().uuid(),
    hasUnreadReplies: z.boolean().nullable(),
    targets: z.array(z.object({ targetId: z.string().uuid(), hasUnreadReplies: z.boolean().nullable(), activityVersion: z.string().nullable() }).strict()),
  })
  .strict();
export type HelpdeskRequestActivity = z.infer<typeof HelpdeskRequestActivity>;
export const HelpdeskRequestActivityResponse = z.object({ items: z.array(HelpdeskRequestActivity) }).strict();
export type HelpdeskRequestActivityResponse = z.infer<typeof HelpdeskRequestActivityResponse>;
export const HELPDESK_UNREAD_COUNT_MAX = 99;
export const HelpdeskUnreadCount = z.object({ count: z.number().int().min(0).max(HELPDESK_UNREAD_COUNT_MAX), capped: z.boolean() }).strict();
export type HelpdeskUnreadCount = z.infer<typeof HelpdeskUnreadCount>;

export const HelpdeskCommentPage = z.object({
  readCursor: HelpdeskReadCursor.nullable().optional(),
  items: z.array(HelpdeskComment),
  nextCursor: NonEmptyString.nullable(),
});
export type HelpdeskCommentPage = z.infer<typeof HelpdeskCommentPage>;

export const HELPDESK_REQUEST_SHARE_SUBJECT_MAX = 20;

export const HelpdeskPortalSharePerson = z
  .object({
    displayName: NonEmptyString,
    email: z.string().email(),
    entraId: NonEmptyString,
  })
  .strict();
export type HelpdeskPortalSharePerson = z.infer<typeof HelpdeskPortalSharePerson>;

export const HelpdeskPortalShareGroup = z
  .object({
    displayName: NonEmptyString,
    id: NonEmptyString,
  })
  .strict();
export type HelpdeskPortalShareGroup = z.infer<typeof HelpdeskPortalShareGroup>;

const uniqueShareSubjectIds = (ids: readonly string[]): boolean => new Set(ids).size === ids.length;

export const PutHelpdeskPortalSharingInput = z
  .object({
    groups: z.array(NonEmptyString).max(HELPDESK_REQUEST_SHARE_SUBJECT_MAX).refine(uniqueShareSubjectIds, "group ids must be unique"),
    people: z.array(NonEmptyString).max(HELPDESK_REQUEST_SHARE_SUBJECT_MAX).refine(uniqueShareSubjectIds, "person ids must be unique"),
  })
  .strict()
  .refine((input) => input.groups.length + input.people.length <= HELPDESK_REQUEST_SHARE_SUBJECT_MAX, `at most ${HELPDESK_REQUEST_SHARE_SUBJECT_MAX} subjects may be shared`);
export type PutHelpdeskPortalSharingInput = z.infer<typeof PutHelpdeskPortalSharingInput>;

export const HelpdeskLinearSharingUser = z
  .object({
    email: z.string().email(),
    guest: z.boolean(),
    id: NonEmptyString,
    name: NonEmptyString,
  })
  .strict();
export type HelpdeskLinearSharingUser = z.infer<typeof HelpdeskLinearSharingUser>;

export const HelpdeskLinearSharingUserSearchResponse = z.object({ users: z.array(HelpdeskLinearSharingUser) }).strict();
export type HelpdeskLinearSharingUserSearchResponse = z.infer<typeof HelpdeskLinearSharingUserSearchResponse>;

export const HelpdeskRequestSharing = z
  .object({
    enabled: z.boolean(),
    portal: z
      .object({
        groups: z.array(HelpdeskPortalShareGroup),
        people: z.array(HelpdeskPortalSharePerson),
      })
      .strict(),
  })
  .strict();
export type HelpdeskRequestSharing = z.infer<typeof HelpdeskRequestSharing>;

export const HelpdeskPortalSharingDirectoryKind = z.enum(["all", "group", "person"]);
export type HelpdeskPortalSharingDirectoryKind = z.infer<typeof HelpdeskPortalSharingDirectoryKind>;

export const HelpdeskPortalSharingDirectoryResponse = z
  .object({
    groups: z.array(HelpdeskPortalShareGroup),
    people: z.array(HelpdeskPortalSharePerson),
  })
  .strict();
export type HelpdeskPortalSharingDirectoryResponse = z.infer<typeof HelpdeskPortalSharingDirectoryResponse>;

export const CreateHelpdeskRequestResponse = z.object({
  requestId: z.string().uuid(),
});
export type CreateHelpdeskRequestResponse = z.infer<typeof CreateHelpdeskRequestResponse>;

export const HelpdeskRequestFieldErrorCode = z.enum([
  "duplicate_option",
  "invalid_date",
  "invalid_datetime",
  "datetime_not_after",
  "datetime_not_before",
  "datetime_outside_time_window",
  "invalid_email",
  "invalid_number",
  "invalid_option",
  "invalid_person",
  "invalid_type",
  "number_out_of_range",
  "number_step_mismatch",
  "temporal_not_in_past",
  "temporal_not_in_recent_range",
  "temporal_not_in_future",
  "temporal_not_far_enough_in_future",
  "required",
  "selection_not_allowed",
  "selection_not_found",
  "too_many_attachments",
  "too_many_group_items",
  "too_long",
  "too_many_people",
  "unknown_field",
]);
export type HelpdeskRequestFieldErrorCode = z.infer<typeof HelpdeskRequestFieldErrorCode>;

export const HelpdeskRequestGroupPathSegment = z.object({ groupId: z.string().uuid(), itemIndex: z.number().int().nonnegative() }).strict();
export type HelpdeskRequestGroupPathSegment = z.infer<typeof HelpdeskRequestGroupPathSegment>;

export const HelpdeskRequestFieldError = z
  .object({
    code: HelpdeskRequestFieldErrorCode,
    fieldId: z.string().min(1),
    groupPath: z.array(HelpdeskRequestGroupPathSegment).min(1).optional(),
    message: NonEmptyString,
  })
  .strict();
export type HelpdeskRequestFieldError = z.infer<typeof HelpdeskRequestFieldError>;

export const HelpdeskRequestValidationErrorResponse = z.object({
  error: z.literal("invalid_values"),
  fieldErrors: z.array(HelpdeskRequestFieldError).min(1),
});
export type HelpdeskRequestValidationErrorResponse = z.infer<typeof HelpdeskRequestValidationErrorResponse>;

export const HelpdeskFormUnavailableErrorResponse = z.object({
  error: z.literal("form_unavailable"),
});
export type HelpdeskFormUnavailableErrorResponse = z.infer<typeof HelpdeskFormUnavailableErrorResponse>;

export const PostHelpdeskCommentInput = z
  .object({
    idempotencyKey: z.string().uuid(),
    body: z.string().trim().max(10_000).optional(),
  })
  .strict();
export type PostHelpdeskCommentInput = z.infer<typeof PostHelpdeskCommentInput>;

export const PostHelpdeskCommentResponse = z.object({
  commentId: NonEmptyString,
});
export type PostHelpdeskCommentResponse = z.infer<typeof PostHelpdeskCommentResponse>;

export const PostHelpdeskCommentErrorResponse = z.object({
  error: z.enum(["conversation_disabled", "issue_terminal"]),
});
export type PostHelpdeskCommentErrorResponse = z.infer<typeof PostHelpdeskCommentErrorResponse>;

export const RetryHelpdeskRequestParams = z.object({
  requestId: z.string().uuid(),
  targetId: z.string().uuid(),
});
export type RetryHelpdeskRequestParams = z.infer<typeof RetryHelpdeskRequestParams>;

export const RetryHelpdeskRequestResponse = z.object({
  requestId: z.string().uuid(),
  targetId: z.string().uuid(),
});
export type RetryHelpdeskRequestResponse = z.infer<typeof RetryHelpdeskRequestResponse>;

export const RetryHelpdeskRequestErrorResponse = z.object({
  error: z.literal("not_retryable"),
});
export type RetryHelpdeskRequestErrorResponse = z.infer<typeof RetryHelpdeskRequestErrorResponse>;

export const HelpdeskWebhookPreviewInput = z
  .object({
    versionId: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    values: z.record(z.string().uuid(), HelpdeskFormValue),
    attachments: z.record(z.string().uuid(), z.array(HelpdeskAttachmentFileName).max(HELPDESK_MAX_ATTACHMENT_REFERENCES)).default({}),
    requesterTimeZone: z.string().min(1).max(100),
    approvalDecision: z.enum(["approved", "denied"]).optional(),
  })
  .strict();
export type HelpdeskWebhookPreviewInput = z.infer<typeof HelpdeskWebhookPreviewInput>;

export const HelpdeskEmailPreference = z.object({ emailEnabled: z.boolean() });
export type HelpdeskEmailPreference = z.infer<typeof HelpdeskEmailPreference>;
export const HelpdeskRequestEmailPreference = HelpdeskEmailPreference.extend({ override: z.boolean().nullable() });
export type HelpdeskRequestEmailPreference = z.infer<typeof HelpdeskRequestEmailPreference>;
